import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardSchool.css';
import { supabase } from '../../lib/supabase';
import { logSchoolActivity, logUserActivity } from '../../lib/activityLogger';
import { UserIdGenerator } from '../../lib/userIdGenerator';
import NotificationModal from '../NotificationModal/NotificationModal';
import { addMonthsToISODate, evaluateSchoolSubscription } from '../../lib/subscription';
import * as XLSX from 'xlsx';

const NoDataState = ({ type, icon, message, actionText, onAction }) => (
  <div className="no-data-state">
    <div className="no-data-icon">{icon}</div>
    <h3>No {type} Found</h3>
    <p>{message}</p>
    {onAction && (
      <button className="add-first-btn" onClick={onAction}>
        {actionText}
      </button>
    )}
  </div>
);

// Grades Management Component with nested subsections
const GradesManagementSection = ({ grades, editingGrade, setEditingGrade, onCreate, onUpdate, onDelete, gradeSections, editingSection, setEditingSection, onCreateSection, onUpdateSection, onDeleteSection }) => {
  const [newGradeName, setNewGradeName] = useState('');
  const [expandedGrades, setExpandedGrades] = useState({});
  const [newSectionForGrade, setNewSectionForGrade] = useState({});

  const handleAddGrade = () => {
    if (!newGradeName.trim()) return;
    // Auto-calculate order based on existing grades count
    const maxOrder = grades.length > 0 ? Math.max(...grades.map(g => g.order_index)) : -1;
    onCreate(newGradeName, maxOrder + 1);
    setNewGradeName('');
  };

  const getSectionsForGrade = (gradeId) => {
    return gradeSections.filter(s => s.grade_id === gradeId).sort((a, b) => a.order_index - b.order_index);
  };

  const handleAddSection = (gradeId) => {
    const sectionData = newSectionForGrade[gradeId];
    if (!sectionData?.name?.trim()) return;
    // Auto-calculate order based on existing sections for this grade
    const sectionsForGrade = getSectionsForGrade(gradeId);
    const maxOrder = sectionsForGrade.length > 0 ? Math.max(...sectionsForGrade.map(s => s.order_index)) : -1;
    onCreateSection(gradeId, sectionData.name, maxOrder + 1);
    setNewSectionForGrade(prev => ({ ...prev, [gradeId]: { name: '' } }));
  };

  const toggleGradeExpansion = (gradeId) => {
    setExpandedGrades(prev => ({ ...prev, [gradeId]: !prev[gradeId] }));
  };

  return (
    <div className="management-section">
      <div className="add-item-form">
        <input
          type="text"
          placeholder="Grade name (e.g., Grade 1)"
          value={newGradeName}
          onChange={(e) => setNewGradeName(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddGrade()}
          style={{ flex: 1 }}
        />
        <button onClick={handleAddGrade} className="add-item-btn">+ Add Grade</button>
      </div>
      <div className="items-list">
        {grades.length === 0 ? (
          <div className="empty-state">No grades added yet</div>
        ) : (
          grades.map(grade => {
            const sections = getSectionsForGrade(grade.id);
            const isExpanded = expandedGrades[grade.id];
            const sectionInput = newSectionForGrade[grade.id] || { name: '' };

            return (
              <div key={grade.id} className="grade-item-container">
                <div className="item-row">
                  {editingGrade?.id === grade.id ? (
                    <GradeEditForm
                      grade={editingGrade}
                      onSave={(name, order, isActive) => {
                        onUpdate(grade.id, name, order, isActive);
                      }}
                      onCancel={() => setEditingGrade(null)}
                    />
                  ) : (
                    <>
                      <div className="item-info" style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            className="expand-btn"
                            onClick={() => toggleGradeExpansion(grade.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                            >
                              <path d="M8.59 16.58L13.17 12L8.59 7.41L10 6L16 12L10 18L8.59 16.58Z" />
                            </svg>
                          </button>
                          <span className="item-name">{grade.name}</span>
                        </div>
                        <span className="item-meta">{sections.length} subsection{sections.length !== 1 ? 's' : ''} • {grade.is_active ? 'Active' : 'Inactive'}</span>
                      </div>
                      <div className="item-actions">
                        <button onClick={() => setEditingGrade(grade)} className="edit-btn">Edit</button>
                        <button onClick={() => onDelete(grade.id)} className="delete-btn">Delete</button>
                      </div>
                    </>
                  )}
                </div>

                {isExpanded && !editingGrade && (
                  <div className="subsections-container">
                    <div className="add-section-form">
                      <input
                        type="text"
                        placeholder="Section name (e.g., Section A)"
                        value={sectionInput.name || ''}
                        onChange={(e) => setNewSectionForGrade(prev => ({ ...prev, [grade.id]: { name: e.target.value } }))}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddSection(grade.id)}
                        style={{ flex: 1 }}
                      />
                      <button onClick={() => handleAddSection(grade.id)} className="add-item-btn" style={{ padding: '6px 12px', fontSize: '0.8125rem' }}>+ Add Section</button>
                    </div>
                    <div className="subsections-list">
                      {sections.length === 0 ? (
                        <div className="empty-state" style={{ padding: '12px', fontSize: '0.8125rem' }}>No subsections for this grade</div>
                      ) : (
                        sections.map(section => (
                          <div key={section.id} className="subsection-row">
                            {editingSection?.id === section.id ? (
                              <SectionEditForm
                                section={editingSection}
                                onSave={(name, order, isActive) => {
                                  onUpdateSection(section.id, name, order, isActive);
                                }}
                                onCancel={() => setEditingSection(null)}
                              />
                            ) : (
                              <>
                                <div className="item-info">
                                  <span className="item-name" style={{ fontSize: '0.875rem' }}>{section.name}</span>
                                  <span className="item-meta">{section.is_active ? 'Active' : 'Inactive'}</span>
                                </div>
                                <div className="item-actions">
                                  <button onClick={() => setEditingSection(section)} className="edit-btn" style={{ padding: '4px 8px', fontSize: '0.8125rem' }}>Edit</button>
                                  <button onClick={() => onDeleteSection(section.id)} className="delete-btn" style={{ padding: '4px 8px', fontSize: '0.8125rem' }}>Delete</button>
                                </div>
                              </>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Grade Edit Form Component
const GradeEditForm = ({ grade, onSave, onCancel }) => {
  const [name, setName] = useState(grade.name);
  const [order, setOrder] = useState(grade.order_index);
  const [isActive, setIsActive] = useState(grade.is_active);

  return (
    <div className="edit-form-inline">
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Grade name" />
      <input
        type="number"
        value={order}
        onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
        style={{ width: '100px' }}
        placeholder="Sort order"
        title="Optional: Used for sorting (lower numbers appear first)"
      />
      <label>
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        Active
      </label>
      <button onClick={() => onSave(name, order, isActive)} className="save-btn">Save</button>
      <button onClick={onCancel} className="cancel-btn">Cancel</button>
    </div>
  );
};

// Grade Sections Management Component
const GradeSectionsManagementSection = ({ grades, gradeSections, selectedGradeForSections, setSelectedGradeForSections, editingSection, setEditingSection, onCreate, onUpdate, onDelete }) => {
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionOrder, setNewSectionOrder] = useState(0);

  const filteredSections = selectedGradeForSections
    ? gradeSections.filter(s => s.grade_id === selectedGradeForSections)
    : [];

  const handleAddSection = () => {
    if (!newSectionName.trim() || !selectedGradeForSections) return;
    onCreate(selectedGradeForSections, newSectionName, newSectionOrder);
    setNewSectionName('');
    setNewSectionOrder(0);
  };

  return (
    <div className="management-section">
      <div className="grade-selector">
        <label>Select Grade:</label>
        <select value={selectedGradeForSections} onChange={(e) => setSelectedGradeForSections(e.target.value)}>
          <option value="">-- Select a grade --</option>
          {grades.map(grade => (
            <option key={grade.id} value={grade.id}>{grade.name}</option>
          ))}
        </select>
      </div>
      {selectedGradeForSections && (
        <>
          <div className="add-item-form">
            <input
              type="text"
              placeholder="Section name (e.g., Section A)"
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddSection()}
            />
            <input
              type="number"
              placeholder="Order"
              value={newSectionOrder}
              onChange={(e) => setNewSectionOrder(parseInt(e.target.value) || 0)}
              style={{ width: '80px' }}
            />
            <button onClick={handleAddSection} className="add-item-btn">+ Add Section</button>
          </div>
          <div className="items-list">
            {filteredSections.length === 0 ? (
              <div className="empty-state">No sections for this grade</div>
            ) : (
              filteredSections.map(section => (
                <div key={section.id} className="item-row">
                  {editingSection?.id === section.id ? (
                    <SectionEditForm
                      section={editingSection}
                      onSave={(name, order, isActive) => {
                        onUpdate(section.id, name, order, isActive);
                      }}
                      onCancel={() => setEditingSection(null)}
                    />
                  ) : (
                    <>
                      <div className="item-info">
                        <span className="item-name">{section.name}</span>
                        <span className="item-meta">Order: {section.order_index} • {section.is_active ? 'Active' : 'Inactive'}</span>
                      </div>
                      <div className="item-actions">
                        <button onClick={() => setEditingSection(section)} className="edit-btn">Edit</button>
                        <button onClick={() => onDelete(section.id)} className="delete-btn">Delete</button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Section Edit Form Component
const SectionEditForm = ({ section, onSave, onCancel }) => {
  const [name, setName] = useState(section.name);
  const [order, setOrder] = useState(section.order_index);
  const [isActive, setIsActive] = useState(section.is_active);

  return (
    <div className="edit-form-inline">
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Section name" />
      <input
        type="number"
        value={order}
        onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
        style={{ width: '100px' }}
        placeholder="Sort order"
        title="Optional: Used for sorting (lower numbers appear first)"
      />
      <label>
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        Active
      </label>
      <button onClick={() => onSave(name, order, isActive)} className="save-btn">Save</button>
      <button onClick={onCancel} className="cancel-btn">Cancel</button>
    </div>
  );
};

// Subjects Management Component
const SubjectsManagementSection = ({ subjects, editingSubject, setEditingSubject, onCreate, onUpdate, onDelete }) => {
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newSubjectDescription, setNewSubjectDescription] = useState('');

  const handleAddSubject = () => {
    if (!newSubjectName.trim()) return;
    onCreate(newSubjectName, newSubjectDescription);
    setNewSubjectName('');
    setNewSubjectDescription('');
  };

  return (
    <div className="management-section">
      <div className="add-item-form subjects-add-form">
        <input
          type="text"
          placeholder="Subject name"
          value={newSubjectName}
          onChange={(e) => setNewSubjectName(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddSubject()}
          className="subject-name-input"
        />
        <input
          type="text"
          placeholder="Description (optional)"
          value={newSubjectDescription}
          onChange={(e) => setNewSubjectDescription(e.target.value)}
          className="subject-description-input"
        />
        <button onClick={handleAddSubject} className="add-item-btn subjects-add-btn">+ Add Subject</button>
      </div>
      <div className="items-list">
        {subjects.length === 0 ? (
          <div className="empty-state">No subjects added yet</div>
        ) : (
          subjects.map(subject => (
            <div key={subject.id} className="item-row">
              {editingSubject?.id === subject.id ? (
                <SubjectEditForm
                  subject={editingSubject}
                  onSave={(name, description, isActive) => {
                    onUpdate(subject.id, name, description, isActive);
                  }}
                  onCancel={() => setEditingSubject(null)}
                />
              ) : (
                <>
                  <div className="item-info">
                    <span className="item-name">{subject.name}</span>
                    {subject.description && <span className="item-description">{subject.description}</span>}
                    <span className="item-meta">{subject.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                  <div className="item-actions">
                    <button onClick={() => setEditingSubject(subject)} className="edit-btn">Edit</button>
                    <button onClick={() => onDelete(subject.id)} className="delete-btn">Delete</button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Subject Edit Form Component
const SubjectEditForm = ({ subject, onSave, onCancel }) => {
  const [name, setName] = useState(subject.name);
  const [description, setDescription] = useState(subject.description || '');
  const [isActive, setIsActive] = useState(subject.is_active);

  return (
    <div className="edit-form-inline">
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Subject name" />
      <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" style={{ flex: 1 }} />
      <label>
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        Active
      </label>
      <button onClick={() => onSave(name, description, isActive)} className="save-btn">Save</button>
      <button onClick={onCancel} className="cancel-btn">Cancel</button>
    </div>
  );
};

// Teachers Hierarchy Component
const TeachersHierarchySection = ({ teachers, editingTeacher, setEditingTeacher, onUpdate }) => {
  const teacherRoles = [
    { value: '', label: 'No Role' },
    { value: 'curriculum_coordinator', label: 'Curriculum Coordinator' },
    { value: 'head_of_department', label: 'Head of Department' },
    { value: 'coordinator', label: 'Coordinator' },
    { value: 'teacher', label: 'Teacher' },
    { value: 'assistant', label: 'Assistant' }
  ];

  const getSupervisorName = (supervisorId) => {
    if (!supervisorId) return 'None';
    const supervisor = teachers.find(t => t.id === supervisorId);
    return supervisor ? `${supervisor.first_name} ${supervisor.last_name}` : 'Unknown';
  };

  return (
    <div className="management-section">
      <div className="teachers-hierarchy">
        {teachers.length === 0 ? (
          <div className="empty-state">No teachers found for this school</div>
        ) : (
          teachers.map(teacher => (
            <div key={teacher.id} className="teacher-row">
              {editingTeacher?.id === teacher.id ? (
                <TeacherEditForm
                  teacher={teacher}
                  teachers={teachers}
                  teacherRoles={teacherRoles}
                  onSave={(role, supervisorId) => {
                    onUpdate(teacher.id, role, supervisorId);
                  }}
                  onCancel={() => setEditingTeacher(null)}
                />
              ) : (
                <>
                  <div className="teacher-info">
                    <span className="teacher-name">{teacher.full_name || `${teacher.first_name} ${teacher.last_name}`}</span>
                    <span className="teacher-email">{teacher.email}</span>
                    <span className="teacher-role">{teacher.teacher_role || 'No role assigned'}</span>
                    {teacher.supervisor_id && (
                      <span className="teacher-supervisor">Supervisor: {getSupervisorName(teacher.supervisor_id)}</span>
                    )}
                  </div>
                  <div className="item-actions">
                    <button onClick={() => setEditingTeacher(teacher)} className="edit-btn">Edit</button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Teacher Edit Form Component
const TeacherEditForm = ({ teacher, teachers, teacherRoles, onSave, onCancel }) => {
  const [role, setRole] = useState(teacher.teacher_role || '');
  const [supervisorId, setSupervisorId] = useState(teacher.supervisor_id || '');

  return (
    <div className="edit-form-inline">
      <select value={role} onChange={(e) => setRole(e.target.value)}>
        {teacherRoles.map(r => (
          <option key={r.value} value={r.value}>{r.label}</option>
        ))}
      </select>
      <select value={supervisorId} onChange={(e) => setSupervisorId(e.target.value)}>
        <option value="">No Supervisor</option>
        {teachers.filter(t => t.id !== teacher.id).map(t => (
          <option key={t.id} value={t.id}>{t.full_name || `${t.first_name} ${t.last_name}`}</option>
        ))}
      </select>
      <button onClick={() => onSave(role || null, supervisorId || null)} className="save-btn">Save</button>
      <button onClick={onCancel} className="cancel-btn">Cancel</button>
    </div>
  );
};

const DashboardSchool = ({ schools, providers, users, dataLoading, onSchoolAdded }) => {
  const navigate = useNavigate();
  const [showAddSchoolModal, setShowAddSchoolModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState('row'); // 'card' or 'row'
  const [filterType, setFilterType] = useState('alphabet');
  const [filterValue, setFilterValue] = useState('');
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedSchoolForUser, setSelectedSchoolForUser] = useState(null);
  const [addUserMode, setAddUserMode] = useState('single'); // 'single' or 'excel'
  const [excelFile, setExcelFile] = useState(null);
  const [isExcelUploading, setIsExcelUploading] = useState(false);
  const [userFormData, setUserFormData] = useState({
    first_name: '',
    last_name: '',
    school_id: '',
    email: '',
    phone: '',
    role: '',
    grade: '',
    // language: '',
    is_active: true,
    password: ''
  });
  const [schoolFormData, setSchoolFormData] = useState({
    name: '',
    school_id: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    website: '',
    // New auth and limits fields
    password: '',
    max_students: '',
    max_teachers: '',
    // Subscription fields (date-only strings)
    subscription_start_date: '',
    subscription_duration_months: '12', // default option: 12 months
    subscription_end_date: '',
    subscription_paid: true,
    subscription_warning_days: '7',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    selectedProviders: []
  });
  const [schoolIdError, setSchoolIdError] = useState('');
  const [isCheckingId, setIsCheckingId] = useState(false);
  const [gradeType, setGradeType] = useState(''); // 'KG' or 'Grade'
  const [gradeLevel, setGradeLevel] = useState(''); // '1', '2', '3' for KG or '1'-'12' for Grade
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [initialUserFormData, setInitialUserFormData] = useState(null);

  // Server-derived user counts per school
  const [userCounts, setUserCounts] = useState({});
  // Server-derived teacher counts per school
  const [teacherCounts, setTeacherCounts] = useState({});

  // School-specific data management states
  const [grades, setGrades] = useState([]);
  const [gradeSections, setGradeSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);

  // Edit states for inline editing
  const [editingGrade, setEditingGrade] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editingSubject, setEditingSubject] = useState(null);
  const [editingTeacher, setEditingTeacher] = useState(null);

  // Collapsible section states
  const [expandedSections, setExpandedSections] = useState({
    grades: false,
    subjects: false,
    teachers: false
  });

  // Expanded School Branches State
  const [expandedSchoolIds, setExpandedSchoolIds] = useState(new Set());

  const toggleSchoolExpanded = (schoolId) => {
    setExpandedSchoolIds(prev => {
      const next = new Set(prev);
      if (next.has(schoolId)) {
        next.delete(schoolId);
      } else {
        next.add(schoolId);
      }
      return next;
    });
  };

  // Generate random 6-digit password
  const generateRandomPassword = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Filter and sorting logic
  useEffect(() => {
    let filtered = [...schools];

    // Apply search filter first
    if (searchQuery.trim()) {
      filtered = filtered.filter(school => {
        const schoolName = (school.name || '').toLowerCase();
        const schoolCode = (school.code || '').toLowerCase();
        const searchTerm = searchQuery.toLowerCase();
        return schoolName.includes(searchTerm) || schoolCode.includes(searchTerm);
      });
    }

    switch (filterType) {
      case 'alphabet':
        filtered = filtered.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      case 'provider':
        if (filterValue) {
          filtered = filtered.filter(school => {
            return school.accessible_providers &&
              school.accessible_providers.some(providerId => {
                const provider = providers.find(p => p.id === providerId);
                return provider && provider.is_active !== false && provider?.name?.toLowerCase().includes(filterValue.toLowerCase());
              });
          });
        }
        // Sort alphabetically after filtering
        filtered = filtered.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      default:
        break;
    }

    setFilteredSchools(filtered);
  }, [schools, filterType, filterValue, providers, searchQuery]);

  // Fetch exact user counts per school from DB
  useEffect(() => {
    const fetchCounts = async () => {
      if (!Array.isArray(schools) || schools.length === 0) {
        setUserCounts({});
        return;
      }
      try {
        const entries = await Promise.all(
          schools.map(async (s) => {
            const { count, error } = await supabase
              .from('users')
              .select('*', { count: 'exact', head: true })
              .eq('school_id', s.id);
            if (error) {}
            return [s.id, count || 0];
          })
        );
        setUserCounts(Object.fromEntries(entries));
      } catch (err) {
        }
    };

    fetchCounts();
  }, [schools]);

  // Fetch exact teacher counts per school from DB
  useEffect(() => {
    const fetchTeacherCounts = async () => {
      if (!Array.isArray(schools) || schools.length === 0) {
        setTeacherCounts({});
        return;
      }
      try {
        const entries = await Promise.all(
          schools.map(async (s) => {
            const { count, error } = await supabase
              .from('users')
              .select('*', { count: 'exact', head: true })
              .eq('school_id', s.id)
              .eq('role', 'teacher');
            if (error) {}
            return [s.id, count || 0];
          })
        );
        setTeacherCounts(Object.fromEntries(entries));
      } catch (err) {
        }
    };
    fetchTeacherCounts();
  }, [schools]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMoreDropdown && !event.target.closest('.more-dropdown')) {
        setShowMoreDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreDropdown]);

  // Reset management sections when modal closes or school changes
  useEffect(() => {
    if (!showDetailsModal || !selectedSchool) {
      setGrades([]);
      setGradeSections([]);
      setSubjects([]);
      setTeachers([]);
      setEditingGrade(null);
      setEditingSection(null);
      setEditingSubject(null);
      setEditingTeacher(null);
      setExpandedSections({
        grades: false,
        subjects: false,
        teachers: false
      });
    }
  }, [showDetailsModal, selectedSchool?.id]);

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  // Check if form has been modified
  const hasFormChanges = () => {
    if (!initialFormData) return false;
    return JSON.stringify(schoolFormData) !== JSON.stringify(initialFormData);
  };

  // Check if user form has been modified
  const hasUserFormChanges = () => {
    if (!initialUserFormData) return false;
    return JSON.stringify(userFormData) !== JSON.stringify(initialUserFormData);
  };

  // Handle overlay click with unsaved changes check
  const handleOverlayClick = (closeAction) => {
    if (hasFormChanges()) {
      setPendingCloseAction(() => closeAction);
      setShowDiscardConfirm(true);
    } else {
      closeAction();
    }
  };

  // Handle user modal overlay click with unsaved changes check
  const handleUserOverlayClick = (closeAction) => {
    if (hasUserFormChanges()) {
      setPendingCloseAction(() => closeAction);
      setShowDiscardConfirm(true);
    } else {
      closeAction();
    }
  };

  // Confirm discard changes
  const confirmDiscard = () => {
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
    setShowDiscardConfirm(false);
    setInitialFormData(null);
  };

  // Cancel discard
  const cancelDiscard = () => {
    setShowDiscardConfirm(false);
    setPendingCloseAction(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setSchoolFormData(prev => {
      const next = { ...prev, [name]: newValue };

      // Auto-compute end date whenever start date or duration changes (if start date is present)
      if (name === 'subscription_start_date' || name === 'subscription_duration_months') {
        const startISO = name === 'subscription_start_date' ? String(newValue || '') : String(prev.subscription_start_date || '');
        const months = Number(name === 'subscription_duration_months' ? newValue : prev.subscription_duration_months);
        if (startISO && Number.isFinite(months) && months > 0) {
          next.subscription_end_date = addMonthsToISODate(startISO, months);
        }
      }

      // If admin sets end date manually, ensure duration reflects it when possible
      // (We keep it simple: no reverse-calculation, just store the explicit end date.)
      return next;
    });

    // Clear school ID error when user starts typing
    if (name === 'school_id') {
      setSchoolIdError('');
    }
  };

  // Check if school ID is unique
  const checkSchoolIdUnique = async (schoolId) => {
    if (!schoolId.trim()) {
      setSchoolIdError('');
      return;
    }

    setIsCheckingId(true);
    try {
      const { data, error } = await supabase
        .from('schools')
        .select('school_id')
        .eq('school_id', schoolId.trim());

      if (error) {setSchoolIdError('Error checking ID availability');
      } else if (data && data.length > 0) {
        setSchoolIdError('This school ID is already taken');
      } else {
        setSchoolIdError('');
      }
    } catch (err) {setSchoolIdError('Error checking ID availability');
    } finally {
      setIsCheckingId(false);
    }
  };

  // Debounced school ID check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (schoolFormData.school_id) {
        checkSchoolIdUnique(schoolFormData.school_id);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [schoolFormData.school_id]);

  const handleProviderToggle = (providerId) => {
    setSchoolFormData(prev => ({
      ...prev,
      selectedProviders: prev.selectedProviders.includes(providerId)
        ? prev.selectedProviders.filter(id => id !== providerId)
        : [...prev.selectedProviders, providerId]
    }));
  };

  // Handle select all/deselect all providers
  const handleSelectAllProviders = () => {
    const activeProviders = providers.filter(provider => provider.is_active !== false);
    const allSelected = activeProviders.length > 0 && 
      activeProviders.every(provider => schoolFormData.selectedProviders.includes(provider.id));
    
    if (allSelected) {
      // Deselect all
      setSchoolFormData(prev => ({
        ...prev,
        selectedProviders: []
      }));
    } else {
      // Select all
      setSchoolFormData(prev => ({
        ...prev,
        selectedProviders: activeProviders.map(provider => provider.id)
      }));
    }
  };

  const openSchoolDetails = (school) => {
    setSelectedSchool(school);
    setIsEditing(false);
    setShowDetailsModal(true);
    // Fetch school-specific data when modal opens
    if (school?.id) {
      fetchSchoolGrades(school.id);
      fetchSchoolSubjects(school.id);
      fetchSchoolTeachers(school.id);
    }
  };

  // Fetch functions for school-specific data
  const fetchSchoolGrades = async (schoolId) => {
    if (!schoolId) return;
    setLoadingGrades(true);
    try {
      const { data, error } = await supabase
        .from('grades')
        .select('*')
        .eq('school_id', schoolId)
        .order('order_index', { ascending: true });

      if (error) throw error;
      setGrades(data || []);

      // Also fetch grade sections for all grades
      if (data && data.length > 0) {
        const gradeIds = data.map(g => g.id);
        fetchGradeSections(gradeIds);
      } else {
        setGradeSections([]);
      }
    } catch (err) {showNotification('error', 'Error Loading Grades', err.message);
    } finally {
      setLoadingGrades(false);
    }
  };

  const fetchGradeSections = async (gradeIds) => {
    if (!gradeIds || gradeIds.length === 0) {
      setGradeSections([]);
      return;
    }
    setLoadingSections(true);
    try {
      const { data, error } = await supabase
        .from('grade_sections')
        .select('*')
        .in('grade_id', gradeIds)
        .order('order_index', { ascending: true });

      if (error) throw error;
      setGradeSections(data || []);
    } catch (err) {showNotification('error', 'Error Loading Grade Sections', err.message);
    } finally {
      setLoadingSections(false);
    }
  };

  const fetchSchoolSubjects = async (schoolId) => {
    if (!schoolId) return;
    setLoadingSubjects(true);
    try {
      const { data, error } = await supabase
        .from('subjects')
        .select('*')
        .eq('school_id', schoolId)
        .order('name', { ascending: true });

      if (error) throw error;
      setSubjects(data || []);
    } catch (err) {showNotification('error', 'Error Loading Subjects', err.message);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const fetchSchoolTeachers = async (schoolId) => {
    if (!schoolId) return;
    setLoadingTeachers(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, first_name, last_name, full_name, email, teacher_role, supervisor_id, is_active')
        .eq('school_id', schoolId)
        .eq('role', 'teacher')
        .order('full_name', { ascending: true });

      if (error) throw error;
      setTeachers(data || []);
    } catch (err) {showNotification('error', 'Error Loading Teachers', err.message);
    } finally {
      setLoadingTeachers(false);
    }
  };

  // CRUD Operations for Grades
  const createGrade = async (name, orderIndex) => {
    if (!selectedSchool?.id) return;
    try {
      const { data, error } = await supabase
        .from('grades')
        .insert([{
          school_id: selectedSchool.id,
          name: name.trim(),
          order_index: orderIndex || 0,
          is_active: true
        }])
        .select()
        .single();

      if (error) throw error;
      setGrades(prev => [...prev, data].sort((a, b) => a.order_index - b.order_index));
      showNotification('success', 'Grade Created', `Grade "${name}" has been created successfully`);
    } catch (err) {showNotification('error', 'Error Creating Grade', err.message);
    }
  };

  const updateGrade = async (id, name, orderIndex, isActive) => {
    try {
      const { error } = await supabase
        .from('grades')
        .update({
          name: name.trim(),
          order_index: orderIndex || 0,
          is_active: isActive !== undefined ? isActive : true
        })
        .eq('id', id);

      if (error) throw error;
      setGrades(prev => prev.map(g => g.id === id ? { ...g, name: name.trim(), order_index: orderIndex || 0, is_active: isActive !== undefined ? isActive : g.is_active } : g)
        .sort((a, b) => a.order_index - b.order_index));
      showNotification('success', 'Grade Updated', 'Grade has been updated successfully');
      setEditingGrade(null);
    } catch (err) {showNotification('error', 'Error Updating Grade', err.message);
    }
  };

  const deleteGrade = async (id) => {
    try {
      // Check if grade has sections
      const { data: sections } = await supabase
        .from('grade_sections')
        .select('id')
        .eq('grade_id', id)
        .limit(1);

      if (sections && sections.length > 0) {
        showNotification('error', 'Cannot Delete Grade', 'This grade has subsections. Please delete all subsections first.');
        return;
      }

      const { error } = await supabase
        .from('grades')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setGrades(prev => prev.filter(g => g.id !== id));
      setGradeSections(prev => prev.filter(s => s.grade_id !== id));
      showNotification('success', 'Grade Deleted', 'Grade has been deleted successfully');
    } catch (err) {showNotification('error', 'Error Deleting Grade', err.message);
    }
  };

  // CRUD Operations for Grade Sections
  const createGradeSection = async (gradeId, name, orderIndex) => {
    try {
      const { data, error } = await supabase
        .from('grade_sections')
        .insert([{
          grade_id: gradeId,
          name: name.trim(),
          order_index: orderIndex || 0,
          is_active: true
        }])
        .select()
        .single();

      if (error) throw error;
      setGradeSections(prev => [...prev, data].sort((a, b) => a.order_index - b.order_index));
      showNotification('success', 'Section Created', `Section "${name}" has been created successfully`);
    } catch (err) {showNotification('error', 'Error Creating Section', err.message);
    }
  };

  const updateGradeSection = async (id, name, orderIndex, isActive) => {
    try {
      const { error } = await supabase
        .from('grade_sections')
        .update({
          name: name.trim(),
          order_index: orderIndex || 0,
          is_active: isActive !== undefined ? isActive : true
        })
        .eq('id', id);

      if (error) throw error;
      setGradeSections(prev => prev.map(s => s.id === id ? { ...s, name: name.trim(), order_index: orderIndex || 0, is_active: isActive !== undefined ? isActive : s.is_active } : s)
        .sort((a, b) => a.order_index - b.order_index));
      showNotification('success', 'Section Updated', 'Section has been updated successfully');
      setEditingSection(null);
    } catch (err) {showNotification('error', 'Error Updating Section', err.message);
    }
  };

  const deleteGradeSection = async (id) => {
    try {
      const { error } = await supabase
        .from('grade_sections')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setGradeSections(prev => prev.filter(s => s.id !== id));
      showNotification('success', 'Section Deleted', 'Section has been deleted successfully');
    } catch (err) {showNotification('error', 'Error Deleting Section', err.message);
    }
  };

  // CRUD Operations for Subjects
  const createSubject = async (name, description) => {
    if (!selectedSchool?.id) return;
    try {
      const { data, error } = await supabase
        .from('subjects')
        .insert([{
          school_id: selectedSchool.id,
          name: name.trim(),
          description: description?.trim() || null,
          is_active: true
        }])
        .select()
        .single();

      if (error) throw error;
      setSubjects(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      showNotification('success', 'Subject Created', `Subject "${name}" has been created successfully`);
    } catch (err) {showNotification('error', 'Error Creating Subject', err.message);
    }
  };

  const updateSubject = async (id, name, description, isActive) => {
    try {
      const { error } = await supabase
        .from('subjects')
        .update({
          name: name.trim(),
          description: description?.trim() || null,
          is_active: isActive !== undefined ? isActive : true
        })
        .eq('id', id);

      if (error) throw error;
      setSubjects(prev => prev.map(s => s.id === id ? { ...s, name: name.trim(), description: description?.trim() || null, is_active: isActive !== undefined ? isActive : s.is_active } : s)
        .sort((a, b) => a.name.localeCompare(b.name)));
      showNotification('success', 'Subject Updated', 'Subject has been updated successfully');
      setEditingSubject(null);
    } catch (err) {showNotification('error', 'Error Updating Subject', err.message);
    }
  };

  const deleteSubject = async (id) => {
    try {
      // Check if subject has teacher assignments
      const { data: teacherSubjects } = await supabase
        .from('teacher_subjects')
        .select('id')
        .eq('subject_id', id)
        .limit(1);

      if (teacherSubjects && teacherSubjects.length > 0) {
        showNotification('error', 'Cannot Delete Subject', 'This subject is assigned to teachers. Please remove all assignments first.');
        return;
      }

      const { error } = await supabase
        .from('subjects')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSubjects(prev => prev.filter(s => s.id !== id));
      showNotification('success', 'Subject Deleted', 'Subject has been deleted successfully');
    } catch (err) {showNotification('error', 'Error Deleting Subject', err.message);
    }
  };

  // Update Teacher Hierarchy
  const updateTeacherRole = async (userId, teacherRole, supervisorId) => {
    try {
      const updateData = {
        teacher_role: teacherRole || null,
        supervisor_id: supervisorId || null,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', userId);

      if (error) throw error;
      setTeachers(prev => prev.map(t => t.id === userId ? { ...t, teacher_role: teacherRole || null, supervisor_id: supervisorId || null } : t));
      showNotification('success', 'Teacher Updated', 'Teacher role and supervisor have been updated successfully');
      setEditingTeacher(null);
      onSchoolAdded && onSchoolAdded();
    } catch (err) {showNotification('error', 'Error Updating Teacher', err.message);
    }
  };

  const startEditSelectedSchool = () => {
    if (!selectedSchool) return;
    // Prime the form with existing values, filtering out inactive providers
    const activeProviders = Array.isArray(selectedSchool.accessible_providers)
      ? selectedSchool.accessible_providers.filter(providerId => {
        const provider = providers.find(p => p.id === providerId);
        return provider && provider.is_active !== false;
      })
      : [];

    setSchoolFormData({
      name: selectedSchool.name || '',
      school_id: selectedSchool.school_id || '',
      address: selectedSchool.contact?.address || '',
      city: selectedSchool.contact?.city || '',
      state: selectedSchool.contact?.state || '',
      zipCode: selectedSchool.contact?.zipCode || '',
      phone: selectedSchool.contact?.phone || '',
      email: selectedSchool.contact?.email || '',
      website: selectedSchool.contact?.website || '',
      password: selectedSchool.password || '',
      max_students: selectedSchool.max_students ?? '',
      max_teachers: selectedSchool.max_teachers ?? '',
      subscription_start_date: selectedSchool.subscription_start_date || '',
      subscription_duration_months: String(selectedSchool.subscription_duration_months ?? '12'),
      subscription_end_date: selectedSchool.subscription_end_date || '',
      subscription_paid: selectedSchool.subscription_paid === undefined || selectedSchool.subscription_paid === null ? true : Boolean(selectedSchool.subscription_paid),
      subscription_warning_days: String(selectedSchool.subscription_warning_days ?? '7'),
      ownerName: selectedSchool.owner?.name || '',
      ownerEmail: selectedSchool.owner?.email || '',
      ownerPhone: selectedSchool.owner?.phone || '',
      selectedProviders: activeProviders
    });
    setIsEditing(true);
  };

  const handleUpdateSchool = async (e) => {
    e.preventDefault();
    if (!selectedSchool) return;
    setIsSubmitting(true);

    if (schoolFormData.selectedProviders.length === 0) {
      alert('Please select at least one provider.');
      setIsSubmitting(false);
      return;
    }

    try {
      const { error } = await supabase
        .from('schools')
        .update({
          name: schoolFormData.name,
          school_id: schoolFormData.school_id,
          password: schoolFormData.password || null,
          max_students: schoolFormData.max_students !== '' ? Number(schoolFormData.max_students) : null,
          max_teachers: schoolFormData.max_teachers !== '' ? Number(schoolFormData.max_teachers) : null,
          subscription_start_date: schoolFormData.subscription_start_date || null,
          subscription_duration_months: schoolFormData.subscription_duration_months !== '' ? Number(schoolFormData.subscription_duration_months) : null,
          subscription_end_date: schoolFormData.subscription_end_date || null,
          subscription_paid: Boolean(schoolFormData.subscription_paid),
          subscription_warning_days: schoolFormData.subscription_warning_days !== '' ? Number(schoolFormData.subscription_warning_days) : 7,
          contact: {
            address: schoolFormData.address,
            city: schoolFormData.city,
            state: schoolFormData.state,
            zipCode: schoolFormData.zipCode,
            phone: schoolFormData.phone,
            email: schoolFormData.email,
            website: schoolFormData.website
          },
          owner: {
            name: schoolFormData.ownerName,
            email: schoolFormData.ownerEmail,
            phone: schoolFormData.ownerPhone
          },
          accessible_providers: schoolFormData.selectedProviders,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedSchool.id);

      if (error) {showNotification('error', 'Error Updating School', error.message);
      } else {
        // Log the activity
        await logSchoolActivity.updated(schoolFormData.name);

        showNotification('success', 'School Updated Successfully', `${schoolFormData.name} has been updated successfully`);
        setShowDetailsModal(false);
        setIsEditing(false);
        onSchoolAdded && onSchoolAdded();
      }
    } catch (err) {showNotification('error', 'Error Updating School', err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSchool = () => {
    if (!selectedSchool) return;
    setItemToDelete({ type: 'school', data: selectedSchool });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      const { error } = await supabase
        .from(itemToDelete.type === 'school' ? 'schools' : 'users')
        .delete()
        .eq('id', itemToDelete.data.id);

      if (error) {showNotification('error', `Error Deleting ${itemToDelete.type === 'school' ? 'School' : 'User'}`, error.message);
      } else {
        // Log the activity
        if (itemToDelete.type === 'school') {
          await logSchoolActivity.deleted(itemToDelete.data.name);
        }

        showNotification('success', `${itemToDelete.type === 'school' ? 'School' : 'User'} Deleted Successfully`, `${itemToDelete.data.name || itemToDelete.data.first_name + ' ' + itemToDelete.data.last_name} has been deleted successfully`);
        setShowDetailsModal(false);
        setSelectedSchool(null);
        setShowDeleteConfirm(false);
        setItemToDelete(null);
        onSchoolAdded && onSchoolAdded();
      }
    } catch (err) {showNotification('error', `Error Deleting ${itemToDelete.type === 'school' ? 'School' : 'User'}`, err.message);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const openAddUserModal = (school) => {
    setSelectedSchoolForUser(school);
    setShowAddUserModal(true);
    setAddUserMode('single');
    const initialData = {
      first_name: '',
      last_name: '',
      school_id: school.id, // Pre-select the school
      email: '',
      phone: '',
      role: '',
      is_active: true
    };
    setUserFormData(initialData);
    setInitialUserFormData(JSON.parse(JSON.stringify(initialData)));
  };

  const closeAddUserModal = () => {
    setShowAddUserModal(false);
    setSelectedSchoolForUser(null);
    setExcelFile(null);
    setUserFormData({
      first_name: '',
      last_name: '',
      school_id: '',
      email: '',
      phone: '',
      role: '',
      grade: '',
      // language: '',
      is_active: true,
      password: ''
    });
    setGradeType('');
    setGradeLevel('');
    setInitialUserFormData(null);
  };

  const handleUserInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'radio' ? value === 'true' : value)
    }));
  };

  const handleGradeTypeChange = (type) => {
    setGradeType(type);
    setGradeLevel(''); // Reset grade level when type changes
    // Update the grade field in userFormData
    setUserFormData(prev => ({
      ...prev,
      grade: '' // Clear grade when type changes
    }));
  };

  const handleGradeLevelChange = (level) => {
    setGradeLevel(level);
    // Update the grade field in userFormData with the full grade name
    const fullGrade = gradeType === 'KG' ? `KG${level}` : `Grade ${level}`;
    setUserFormData(prev => ({
      ...prev,
      grade: fullGrade
    }));
  };

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    const name = (file?.name || '').toLowerCase();
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
    if (file && isExcel) {
      setExcelFile(file);
    } else {
      alert('Please select a valid Excel file (.xlsx or .xls)');
    }
  };

  const normalizeExcelHeader = (header) => {
    if (header === null || header === undefined) return '';
    return String(header)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  };

  const getExcelValue = (row, keys) => {
    for (const k of keys) {
      if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== '') {
        return row[k];
      }
    }
    return '';
  };

  const readExcelFirstSheet = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames?.[0];
          if (!firstSheetName) return resolve([]);
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          resolve(rows || []);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read the Excel file'));
      reader.readAsArrayBuffer(file);
    });

  const normalizeGrade = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const lower = raw.toLowerCase();
    if (lower.startsWith('kg')) {
      // Accept KG1 / KG 1
      const num = raw.replace(/kg/i, '').trim().replace(/\s+/g, '');
      return num ? `KG${num}` : raw;
    }
    if (lower.startsWith('grade')) {
      const num = raw.replace(/grade/i, '').trim();
      return num ? `Grade ${num}` : raw;
    }
    // If it's just a number, assume Grade
    if (/^\d+$/.test(raw)) return `Grade ${raw}`;
    return raw;
  };

  // Supports values like: "Grade 5, Grade 8 and Grade 10"
  const normalizeGradeList = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const parts = raw
      .split(/,|and/i)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length <= 1) return normalizeGrade(raw);
    const normalizedParts = parts.map((p) => normalizeGrade(p));
    return normalizedParts.join(', ');
  };

  const parseIsActive = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return true; // default active
    if (['active', 'true', 'yes', 'y', '1'].includes(raw)) return true;
    if (['inactive', 'false', 'no', 'n', '0'].includes(raw)) return false;
    return true;
  };

  const splitFullName = (value) => {
    const raw = String(value || '').trim().replace(/\s+/g, ' ');
    if (!raw) return { first_name: '', last_name: '' };
    const parts = raw.split(' ');
    if (parts.length === 1) return { first_name: parts[0], last_name: '' };
    return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
  };

  const handleSingleUserSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Generate unique user_id based on school_id
      const generatedUserId = await UserIdGenerator.generateUserId(selectedSchoolForUser.school_id);

      if (!generatedUserId) {
        alert('Error generating user ID. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Generate random 6-digit password
      const randomPassword = generateRandomPassword();

      const { error } = await supabase
        .from('users')
        .insert([{
          user_id: generatedUserId,
          first_name: userFormData.first_name,
          last_name: userFormData.last_name,
          school_id: userFormData.school_id || null,
          email: userFormData.email,
          phone: userFormData.phone || null,
          role: userFormData.role || null,
          grade: userFormData.grade || null,
          // language: userFormData.language || null,
          is_active: userFormData.is_active,
          password: randomPassword,
          created_at: new Date().toISOString()
        }])
        .select();

      if (error) {showNotification('error', 'Error Adding User', error.message);
      } else {
        // Log the activity
        const schoolName = schools.find(s => s.id === userFormData.school_id)?.name || 'Unknown School';
        const userName = `${userFormData.first_name} ${userFormData.last_name}`;
        await logUserActivity.created(userName, schoolName);

        showNotification('success', 'User Added Successfully', `${userName} has been added with ID: ${generatedUserId} and Password: ${randomPassword}`);
        setUserFormData({
          first_name: '', last_name: '', school_id: '', email: '', phone: '', role: '', grade: '', is_active: true, password: ''
        });
        setInitialUserFormData(null);
        setShowAddUserModal(false);
        onSchoolAdded && onSchoolAdded();
      }
    } catch (err) {showNotification('error', 'Error Adding User', err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExcelUpload = async () => {
    if (!excelFile || !selectedSchoolForUser) return;
    setIsExcelUploading(true);

    try {
      const rows = await readExcelFirstSheet(excelFile);
      if (!rows || rows.length === 0) {
        showNotification('error', 'Excel Import Failed', 'Your Excel file is empty (no rows found).');
        return;
      }

      const schoolUuid = selectedSchoolForUser.id; // UUID from schools table
      const schoolName = selectedSchoolForUser.name || 'Unknown School';
      const schoolCode = (selectedSchoolForUser.school_id || '').trim().toLowerCase();
      if (!schoolUuid || !schoolCode) {
        showNotification('error', 'Excel Import Failed', 'Missing school information. Please try again.');
        return;
      }

      // Determine next user number once, then generate sequential IDs locally (fast + stable)
      let nextNumber = await UserIdGenerator.getNextUserNumber(schoolCode);
      if (!nextNumber || Number.isNaN(nextNumber)) nextNumber = 1;

      const allowedRoles = new Set(['student', 'teacher', 'admin']);
      const toInsert = [];
      const skipped = [];
      const warnings = [];
      const seenUserIds = new Set();

      for (let i = 0; i < rows.length; i++) {
        const originalRow = rows[i];
        const normalizedRow = {};
        Object.entries(originalRow || {}).forEach(([k, v]) => {
          const nk = normalizeExcelHeader(k);
          if (nk) normalizedRow[nk] = typeof v === 'string' ? v.trim() : v;
        });

        // Support your Excel format:
        // userId | role | fullName | email | password | language | grade | school | status
        const providedUserIdRaw = String(
          getExcelValue(normalizedRow, ['userid', 'user_id', 'usercode', 'user_code'])
        ).trim();

        const fullNameRaw = String(
          getExcelValue(normalizedRow, ['fullname', 'full_name', 'name'])
        ).trim();
        const split = splitFullName(fullNameRaw);

        const firstName = String(
          getExcelValue(normalizedRow, ['first_name', 'firstname', 'first'])
        ).trim() || split.first_name;
        const lastName = String(
          getExcelValue(normalizedRow, ['last_name', 'lastname', 'last', 'surname'])
        ).trim() || split.last_name;

        const email = String(
          getExcelValue(normalizedRow, ['email', 'email_address', 'mail'])
        ).trim();
        const phone = String(
          getExcelValue(normalizedRow, ['phone', 'phone_number', 'mobile'])
        ).trim();
        const roleRaw = String(
          getExcelValue(normalizedRow, ['role', 'user_role', 'type'])
        )
          .trim()
          .toLowerCase();
        const role = roleRaw === 'staff' ? 'admin' : roleRaw; // small convenience mapping
        const grade = normalizeGradeList(getExcelValue(normalizedRow, ['grade', 'class', 'level']));
        // const language = String(getExcelValue(normalizedRow, ['language', 'lang'])).trim();
        const subject = String(getExcelValue(normalizedRow, ['subject', 'subjects'])).trim();
        const statusRaw = getExcelValue(normalizedRow, ['status', 'is_active', 'active']);
        const isActive = parseIsActive(statusRaw);

        // If the Excel contains a "school" column, only import rows matching selected school (prevents accidental cross-school import)
        const schoolCell = String(getExcelValue(normalizedRow, ['school', 'school_name', 'schoolid', 'school_id_text', 'school_code'])).trim();
        if (schoolCell) {
          const cellLower = schoolCell.toLowerCase();
          const matchesSelected =
            cellLower === schoolCode ||
            cellLower === schoolName.toLowerCase();
          if (!matchesSelected) {
            skipped.push(`Row ${i + 2}: school "${schoolCell}" does not match selected school "${schoolName}" (${schoolCode})`);
            continue;
          }
        }

        // Skip completely blank rows
        if (!firstName && !lastName && !email && !role && !fullNameRaw && !providedUserIdRaw) continue;

        // Validate minimum requirements
        if (!email || !role || (!firstName && !lastName)) {
          skipped.push(`Row ${i + 2}: missing Name / Email / Role`);
          continue;
        }
        if (!allowedRoles.has(role)) {
          skipped.push(`Row ${i + 2}: invalid role "${roleRaw}" (use: student, teacher, admin)`);
          continue;
        }

        // Teachers must have a subject
        if (role === 'teacher' && !subject) {
          skipped.push(`Row ${i + 2}: teacher is missing Subject`);
          continue;
        }

        // Use provided userId if present, otherwise generate one
        const userId = providedUserIdRaw || `${schoolCode}${nextNumber++}`;
        if (seenUserIds.has(userId)) {
          skipped.push(`Row ${i + 2}: duplicate userId "${userId}" in Excel`);
          continue;
        }
        seenUserIds.add(userId);

        if (!providedUserIdRaw && !userId.startsWith(schoolCode)) {
          // This won't happen with our generator, but keep the guard
          warnings.push(`Row ${i + 2}: generated userId "${userId}" does not start with "${schoolCode}"`);
        }
        if (providedUserIdRaw && !providedUserIdRaw.toLowerCase().startsWith(schoolCode)) {
          warnings.push(`Row ${i + 2}: userId "${providedUserIdRaw}" does not start with "${schoolCode}" (this may be OK, but some parts of the app expect the school prefix).`);
        }

        // Use provided password if present, otherwise generate a 6-digit one
        const providedPassword = String(getExcelValue(normalizedRow, ['password', 'pass'])).trim();
        const finalPassword = providedPassword || generateRandomPassword();

        const payload = {
          user_id: userId,
          first_name: firstName,
          last_name: lastName,
          school_id: schoolUuid,
          email,
          phone: phone || null,
          role,
          grade: grade || null,
          // language: language || null,
          is_active: isActive,
          password: finalPassword,
          created_at: new Date().toISOString()
        };

        // Optional fields used elsewhere in the app (only include if provided)
        if (role === 'teacher' && subject) {
          payload.subject = subject;
        }

        toInsert.push(payload);
      }

      if (toInsert.length === 0) {
        showNotification(
          'error',
          'Excel Import Failed',
          skipped.length
            ? `No valid users found. Issues:\n${skipped.slice(0, 8).join('\n')}${skipped.length > 8 ? '\n...' : ''}`
            : 'No valid users found in the file.'
        );
        return;
      }

      // Pre-check DB duplicates for provided user IDs (avoid hard-to-read insert errors)
      const idsToCheck = Array.from(seenUserIds);
      const existingIds = new Set();
      const CHECK_CHUNK = 200;
      for (let i = 0; i < idsToCheck.length; i += CHECK_CHUNK) {
        const chunk = idsToCheck.slice(i, i + CHECK_CHUNK);
        const { data, error } = await supabase.from('users').select('user_id').in('user_id', chunk);
        if (error) {// not fatal; continue
        } else {
          (data || []).forEach((u) => existingIds.add(u.user_id));
        }
      }
      if (existingIds.size > 0) {
        // Filter out duplicates before insert
        const before = toInsert.length;
        const filtered = toInsert.filter((u) => !existingIds.has(u.user_id));
        const removed = before - filtered.length;
        if (removed > 0) {
          warnings.push(`${removed} user(s) were skipped because their userId already exists in the database.`);
        }
        if (filtered.length === 0) {
          showNotification('error', 'Excel Import Failed', 'All rows were skipped because the userIds already exist in the database.');
          return;
        }
        toInsert.length = 0;
        toInsert.push(...filtered);
      }

      // Insert in chunks to avoid payload limits
      const CHUNK_SIZE = 50;
      let insertedCount = 0;
      for (let i = 0; i < toInsert.length; i += CHUNK_SIZE) {
        const chunk = toInsert.slice(i, i + CHUNK_SIZE);
        const { error } = await supabase.from('users').insert(chunk);
        if (error) {showNotification('error', 'Excel Import Failed', error.message);
          return;
        }
        insertedCount += chunk.length;
      }

      // Log one summary activity (admin + school-specific)
      const summaryName = `${insertedCount} users imported (Excel)`;
      await logUserActivity.created(summaryName, schoolName);
      await logUserActivity.created(summaryName, schoolName, schoolUuid);

      // Refresh dashboard data so DashboardUsers + school dashboards reflect imported users
      onSchoolAdded && onSchoolAdded();

      // Success message
      const skippedNote =
        skipped.length > 0
          ? `\n\nSkipped ${skipped.length} row(s):\n${skipped.slice(0, 8).join('\n')}${skipped.length > 8 ? '\n...' : ''}`
          : '';
      const warningNote =
        warnings.length > 0
          ? `\n\nNotes:\n${warnings.slice(0, 8).join('\n')}${warnings.length > 8 ? '\n...' : ''}`
          : '';

      showNotification(
        'success',
        'Excel Import Complete',
        `Imported ${insertedCount} user(s) into "${schoolName}".${skippedNote}${warningNote}`
      );

      setInitialUserFormData(null);
      closeAddUserModal();
      setExcelFile(null);
    } catch (err) {showNotification('error', 'Excel Import Failed', err.message || 'Unable to import the Excel file.');
    } finally {
      setIsExcelUploading(false);
    }
  };

  const handleSubmitSchool = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (schoolFormData.selectedProviders.length === 0) {
      showNotification('warning', 'Provider Selection Required', 'Please select at least one provider.');
      setIsSubmitting(false);
      return;
    }

    if (schoolIdError) {
      showNotification('error', 'School ID Error', 'Please fix the school ID error before submitting.');
      setIsSubmitting(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('schools')
        .insert([{
          name: schoolFormData.name,
          school_id: schoolFormData.school_id,
          password: schoolFormData.password || null,
          max_students: schoolFormData.max_students !== '' ? Number(schoolFormData.max_students) : null,
          max_teachers: schoolFormData.max_teachers !== '' ? Number(schoolFormData.max_teachers) : null,
          subscription_start_date: schoolFormData.subscription_start_date || null,
          subscription_duration_months: schoolFormData.subscription_duration_months !== '' ? Number(schoolFormData.subscription_duration_months) : null,
          subscription_end_date: schoolFormData.subscription_end_date || null,
          subscription_paid: Boolean(schoolFormData.subscription_paid),
          subscription_warning_days: schoolFormData.subscription_warning_days !== '' ? Number(schoolFormData.subscription_warning_days) : 7,
          contact: {
            address: schoolFormData.address,
            city: schoolFormData.city,
            state: schoolFormData.state,
            zipCode: schoolFormData.zipCode,
            phone: schoolFormData.phone,
            email: schoolFormData.email,
            website: schoolFormData.website
          },
          owner: {
            name: schoolFormData.ownerName,
            email: schoolFormData.ownerEmail,
            phone: schoolFormData.ownerPhone
          },
          accessible_providers: schoolFormData.selectedProviders,
          settings: {},
          created_at: new Date().toISOString()
        }])
        .select();

      if (error) {showNotification('error', 'Error Adding School', error.message);
      } else {
        const newSchoolId = data[0]?.id;

        // Auto-populate default grades and subjects from system_settings (if any exist)
        if (newSchoolId) {
          try {
            const { data: systemSettings } = await supabase
              .from('system_settings')
              .select('available_grades, available_subjects')
              .eq('id', 1)
              .single();

            const defaultGrades = systemSettings?.available_grades || [];
            const defaultSubjects = systemSettings?.available_subjects || [];

            // Insert default grades
            if (defaultGrades.length > 0) {
              // Handle both new JSONB format {grade: "...", order: ...} and old text array format
              const gradesInsert = defaultGrades.map((gradeItem, index) => {
                let gradeName;
                let orderIndex;

                // Check if it's the new JSONB format
                if (typeof gradeItem === 'object' && gradeItem !== null) {
                  gradeName = gradeItem.grade || gradeItem.name || '';
                  orderIndex = gradeItem.order !== undefined ? gradeItem.order : index;
                } else {
                  // Old format (text array) - backward compatibility
                  gradeName = typeof gradeItem === 'string' ? gradeItem : '';
                  orderIndex = index;
                }

                return {
                  school_id: newSchoolId,
                  name: gradeName,
                  order_index: orderIndex,
                  is_active: true
                };
              });

              await supabase.from('grades').insert(gradesInsert);}

            // Insert default subjects
            if (defaultSubjects.length > 0) {
              const subjectsInsert = defaultSubjects.map((subjectName) => ({
                school_id: newSchoolId,
                name: subjectName,
                is_active: true
              }));

              await supabase.from('subjects').insert(subjectsInsert);}
          } catch (populateError) {// Don't fail school creation if defaults insertion fails
          }
        }

        // Log the activity
        await logSchoolActivity.created(schoolFormData.name);

        showNotification('success', 'School Added Successfully', `${schoolFormData.name} has been added successfully`);
        setSchoolFormData({
          name: '', school_id: '', address: '', city: '', state: '', zipCode: '', phone: '', email: '', website: '',
          password: '', max_students: '', max_teachers: '',
          subscription_start_date: '',
          subscription_duration_months: '12',
          subscription_end_date: '',
          subscription_paid: true,
          subscription_warning_days: '7',
          ownerName: '', ownerEmail: '', ownerPhone: '', selectedProviders: []
        });
        setSchoolIdError('');
        setInitialFormData(null);
        setShowAddSchoolModal(false);
        onSchoolAdded && onSchoolAdded();
      }
    } catch (err) {showNotification('error', 'Error Adding School', err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowAddSchoolModal(false);
    setSchoolFormData({
      name: '', school_id: '', address: '', city: '', state: '', zipCode: '', phone: '', email: '', website: '',
      password: '', max_students: '', max_teachers: '',
      subscription_start_date: '',
      subscription_duration_months: '12',
      subscription_end_date: '',
      subscription_paid: true,
      subscription_warning_days: '7',
      ownerName: '', ownerEmail: '', ownerPhone: '', selectedProviders: []
    });
    setSchoolIdError('');
    setInitialFormData(null);
  };

  // Set initial form data when modal opens
  useEffect(() => {
    if (showAddSchoolModal) {
      setInitialFormData(JSON.parse(JSON.stringify(schoolFormData)));
    } else {
      setInitialFormData(null);
    }
  }, [showAddSchoolModal]);

  // Reset initial form data when modal closes successfully
  useEffect(() => {
    if (!showAddSchoolModal && !showAddUserModal) {
      setInitialFormData(null);
      setInitialUserFormData(null);
    }
  }, [showAddSchoolModal, showAddUserModal]);

  const getProviderNames = (providerIds) => {
    if (!providerIds || !Array.isArray(providerIds) || providerIds.length === 0) return [];

    const validProviders = providerIds
      .map(id => providers.find(p => p.id === id))
      .filter(provider => provider && provider.name && provider.is_active !== false)
      .map(provider => provider.name);

    return validProviders.length > 0 ? validProviders : [];
  };

  const getSubscriptionBadge = (school) => {
    const sub = evaluateSchoolSubscription(school);
    let text = 'Active';
    switch (sub.status) {
      case 'expiring':
        text = `Expiring${typeof sub.daysRemaining === 'number' ? ` (${sub.daysRemaining}d)` : ''}`;
        break;
      case 'expired':
        text = 'Expired';
        break;
      case 'unpaid':
        text = 'Unpaid';
        break;
      case 'not_started':
        text = 'Not Started';
        break;
      case 'legacy':
      case 'active':
      default:
        text = 'Active';
        break;
    }

    const titleParts = [];
    if (school?.subscription_start_date) titleParts.push(`Start: ${school.subscription_start_date}`);
    if (school?.subscription_end_date) titleParts.push(`End: ${school.subscription_end_date}`);
    if (school?.subscription_paid === false) titleParts.push('Paid: No');
    if (school?.subscription_paid === true) titleParts.push('Paid: Yes');
    const title = titleParts.join(' • ');

    return (
      <span className={`subscription-badge ${sub.status}`} title={title}>
        {text}
      </span>
    );
  };


  // SVG Icons
  const SchoolIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" fill="currentColor" />
    </svg>
  );

  const CardViewIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4ZM4 6V18H20V6H4Z" fill="currentColor" />
      <path d="M4 8H8V12H4V8Z" fill="currentColor" />
      <path d="M10 8H14V12H10V8Z" fill="currentColor" />
      <path d="M16 8H20V12H16V8Z" fill="currentColor" />
      <path d="M4 14H8V18H4V14Z" fill="currentColor" />
      <path d="M10 14H14V18H10V14Z" fill="currentColor" />
      <path d="M16 14H20V18H16V14Z" fill="currentColor" />
    </svg>
  );

  const RowViewIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 6H20V8H4V6ZM4 11H20V13H4V11ZM4 16H20V18H4V16Z" fill="currentColor" />
    </svg>
  );

  const FilterIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 18H14V16H10V18ZM3 6V8H21V6H3ZM6 13H18V11H6V13Z" fill="currentColor" />
    </svg>
  );

  const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3S3 5.91 3 9.5S5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5S14 7.01 14 9.5S11.99 14 9.5 14Z" fill="currentColor" />
    </svg>
  );

  const MoreIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 8C13.1 8 14 7.1 14 6C14 4.9 13.1 4 12 4C10.9 4 10 4.9 10 6C10 7.1 10.9 8 12 8ZM12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12C14 10.9 13.1 10 12 10ZM12 16C10.9 16 10 16.9 10 18C10 19.1 10.9 20 12 20C13.1 20 14 19.1 14 18C14 16.9 13.1 16 12 16Z" fill="currentColor" />
    </svg>
  );

  const PlusIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="currentColor" />
    </svg>
  );

  const UserIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor" />
    </svg>
  );

  const ExcelIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z" fill="currentColor" />
      <path d="M8 12H16V14H8V12ZM8 16H16V18H8V16Z" fill="currentColor" />
    </svg>
  );

  const getUserCount = (schoolId) => {
    if (!schoolId) return 0;
    if (userCounts && Object.prototype.hasOwnProperty.call(userCounts, schoolId)) {
      return userCounts[schoolId] || 0;
    }
    // Fallback to in-memory users list if provided
    if (users && Array.isArray(users)) {
      const schoolUsers = users.filter(user => (
        user.school_id === schoolId ||
        user.school_id === schoolId ||
        String(user.school_id) === String(schoolId)
      ));
      return schoolUsers.length;
    }
    return 0;
  };

  const getTeacherCount = (schoolId) => {
    if (!schoolId) return 0;
    if (teacherCounts && Object.prototype.hasOwnProperty.call(teacherCounts, schoolId)) {
      return teacherCounts[schoolId] || 0;
    }
    // Fallback to in-memory users list if provided
    if (users && Array.isArray(users)) {
      const schoolTeachers = users.filter(user => (
        (user.school_id === schoolId ||
          user.school_id === schoolId ||
          String(user.school_id) === String(schoolId)) &&
        user.role === 'teacher'
      ));
      return schoolTeachers.length;
    }
    return 0;
  };

  // Group schools by hierarchy (Main -> Branches)
  const structuredSchools = React.useMemo(() => {
    const schoolMap = new Map();
    const roots = [];

    // First pass: map all filtered schools
    filteredSchools.forEach(s => {
      schoolMap.set(s.id, { ...s, branches: [] });
    });

    // Second pass: build hierarchy
    filteredSchools.forEach(s => {
      const current = schoolMap.get(s.id);
      if (s.parent_school_id && schoolMap.has(s.parent_school_id)) {
        // It is a branch of a school ALSO in the filtered list
        schoolMap.get(s.parent_school_id).branches.push(current);
      } else {
        // It is a main school (or orphaned branch in this view)
        roots.push(current);
      }
    });

    return roots;
  }, [filteredSchools]);

  return (
    <div className="schools-content">
      {/* Discard Changes Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="unsaved-changes-overlay" onClick={cancelDiscard}>
          <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-changes-header">
              <div className="unsaved-changes-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Unsaved Changes</h2>
            </div>
            <div className="unsaved-changes-body">
              <p className="unsaved-changes-message">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>
            <div className="unsaved-changes-actions">
              <button className="unsaved-changes-cancel-btn" onClick={cancelDiscard}>
                Cancel
              </button>
              <button className="unsaved-changes-discard-btn" onClick={confirmDiscard}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="content-header">
        <h2>Schools Directory ({filteredSchools.length} of {schools.length})</h2>
        <div className="header-actions">
          <div className="more-dropdown">
            <button
              className="more-button"
              onClick={() => setShowMoreDropdown(!showMoreDropdown)}
            >
              <MoreIcon />
              More
            </button>
            {showMoreDropdown && (
              <div className="dropdown-menu">
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setViewMode(viewMode === 'row' ? 'card' : 'row');
                    setShowMoreDropdown(false);
                  }}
                >
                  {viewMode === 'row' ? <CardViewIcon /> : <RowViewIcon />}
                  {viewMode === 'row' ? 'Switch to Card View' : 'Switch to Row View'}
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowFilterModal(true);
                    setShowMoreDropdown(false);
                  }}
                >
                  <FilterIcon />
                  Filter
                </button>
              </div>
            )}
          </div>
          <button className="add-button" onClick={() => setShowAddSchoolModal(true)}>+ Add School</button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="dashboard-school-search-container">
          <SearchIcon />
          <input
            type="text"
            placeholder="Search schools by name or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="dashboard-school-search-input"
          />
          {searchQuery && (
            <button
              className="clear-search"
              onClick={() => setSearchQuery('')}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {dataLoading ? (
        <div className="loading-state">
          <div className="loading-spinner">⏳</div>
          <p>Loading schools...</p>
        </div>
      ) : filteredSchools.length === 0 ? (
        <NoDataState
          type="Schools"
          icon="🏫"
          message={schools.length === 0 ? "No schools have been added yet. Add your first school to get started." : "No schools match the current filter criteria."}
          actionText={schools.length === 0 ? "+ Add First School" : "Clear Filters"}
          onAction={schools.length === 0 ? () => setShowAddSchoolModal(true) : () => {
            setFilterType('alphabet');
            setFilterValue('');
          }}
        />
      ) : viewMode === 'card' ? (
        <div className="schools-grid">
          {filteredSchools.map((school) => (
            <div key={school.id} className="school-card" onClick={() => openSchoolDetails(school)}>
              <div className="school-logo">
                <SchoolIcon />
              </div>
              <h3>{school.name || 'Unnamed School'}</h3>
              <div className="school-subscription-row">
                {getSubscriptionBadge(school)}
              </div>
              <p>Code: {school.code || 'N/A'} • ID: {school.unique_id || 'N/A'}</p>
              <div className="school-stats">
                <span>Owner: {school.owner?.name || 'Unknown'}</span>
                {school.contact?.phone && <span>Phone: {school.contact.phone}</span>}
              </div>
              <div className="school-location">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '4px' }}>
                  <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9S10.62 6.5 12 6.5S14.5 7.62 14.5 9S13.38 11.5 12 11.5Z" />
                </svg>
                {school.contact?.city || 'Unknown'}, {school.contact?.state || 'Unknown'}
              </div>
              {school.accessible_providers && school.accessible_providers.length > 0 && (
                <div className="school-providers">
                  <span>Providers: {school.accessible_providers.length}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="schools-table">
          <div className="table-header">
            <div className="table-cell header-cell">School ID</div>
            <div className="table-cell header-cell">School Name</div>
            <div className="table-cell header-cell">Subscription</div>
            <div className="table-cell header-cell">Providers</div>
            <div className="table-cell header-cell">Users</div>
            {/* <div className="table-cell header-cell">Teachers</div> */}
          </div>

          {structuredSchools.map((school) => {
            const hasBranches = school.branches && school.branches.length > 0;
            const isExpanded = expandedSchoolIds.has(school.id);

            return (
              <React.Fragment key={school.id}>
                {/* Main School Row */}
                <div className="table-row" onClick={() => openSchoolDetails(school)}>
                  <div className="table-cell data-cell">
                    <div className="school-id-info">
                      <div className="school-id-main-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {hasBranches && (
                          <button
                            className="expand-branches-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSchoolExpanded(school.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#64748b',
                              transition: 'transform 0.2s'
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                            >
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                          </button>
                        )}
                        <div className="school-id-main" style={{ marginLeft: hasBranches ? '0' : '24px' }}>
                          {school.school_id || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="table-cell data-cell">
                    <div className="school-name-info">
                      <div className="school-name-main">{school.name || 'Unnamed School'}</div>
                    </div>
                  </div>
                  <div className="table-cell data-cell">
                    {getSubscriptionBadge(school)}
                  </div>
                  <div className="table-cell data-cell providers-cell">
                    <div className="providers-list">
                      {school.accessible_providers && school.accessible_providers.length > 0 ? (
                        getProviderNames(school.accessible_providers).map((providerName, index) => (
                          <span key={index} className="provider-tag">
                            {providerName}
                          </span>
                        ))
                      ) : (
                        <span className="provider-tag unknown-provider">Unknown Provider</span>
                      )}
                    </div>
                  </div>
                  <div className="table-cell data-cell user-count-cell">
                    <div className="user-count-info">
                      <div className="user-count-number">{getUserCount(school.id)}</div>
                      <div className="user-count-label">users</div>
                    </div>
                  </div>
                </div>

                {/* Branch Rows */}
                {isExpanded && school.branches.map((branch) => (
                  <div key={branch.id} className="table-row branch-row" onClick={() => openSchoolDetails(branch)}>
                    <div className="table-cell data-cell">
                      <div className="school-id-info branch-indicator">
                        <div className="branch-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 14L4 9l5-5" />
                            <path d="M4 9h10c1.2 0 2.2.8 2.2 2v6" />
                          </svg>
                        </div>
                        <div className="school-id-main">{branch.school_id || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="table-cell data-cell">
                      <div className="school-name-info">
                        <div className="school-name-main">{branch.name || 'Unnamed Branch'}</div>
                      </div>
                    </div>
                    <div className="table-cell data-cell">
                      {getSubscriptionBadge(branch)}
                    </div>
                    <div className="table-cell data-cell providers-cell">
                      <div className="providers-list">
                        {branch.accessible_providers && branch.accessible_providers.length > 0 ? (
                          getProviderNames(branch.accessible_providers).map((providerName, index) => (
                            <span key={index} className="provider-tag">
                              {providerName}
                            </span>
                          ))
                        ) : (
                          <span className="provider-tag unknown-provider">Unknown Provider</span>
                        )}
                      </div>
                    </div>
                    <div className="table-cell data-cell user-count-cell">
                      <div className="user-count-info">
                        <div className="user-count-number">{getUserCount(branch.id)}</div>
                        <div className="user-count-label">users</div>
                      </div>
                    </div>
                  </div>
                ))}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* School Details / Edit Modal */}
      {showDetailsModal && selectedSchool && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditing ? 'Edit School' : 'School Details'}</h2>
              <div className="modal-header-actions">
                <button
                  className="modal-expand"
                  onClick={() => {
                    navigate(`/school/${selectedSchool.id}`);
                  }}
                  title="Expand to Full Page"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                  </svg>
                </button>
                <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
              </div>
            </div>

            {!isEditing ? (
              <div className="school-details">
                <div className="details-section">
                  <div className="section-header">
                    <div className="section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" />
                      </svg>
                    </div>
                    <h4>Basic Information</h4>
                  </div>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="detail-label">Name</span>
                      <span className="detail-value">{selectedSchool.name || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">School ID</span>
                      <span className="detail-value">{selectedSchool.school_id || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Password</span>
                      <span className="detail-value">{selectedSchool.password || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Max Students</span>
                      <span className="detail-value">{(selectedSchool.max_students ?? '') !== '' ? selectedSchool.max_students : '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Max Teachers</span>
                      <span className="detail-value">{(selectedSchool.max_teachers ?? '') !== '' ? selectedSchool.max_teachers : '—'}</span>
                    </div>
                  </div>
                </div>

                <div className="section-divider"></div>

                <div className="details-section">
                  <div className="section-header">
                    <div className="section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12S6.48 22 12 22 22 17.52 22 12 17.52 2 12 2zm1 17.93c-2.83.48-5.5-.3-7.47-2.27C3.56 15.69 2.78 13.02 3.26 10.19 3.86 6.7 6.7 3.86 10.19 3.26c2.83-.48 5.5.3 7.47 2.27 1.97 1.97 2.75 4.64 2.27 7.47-.6 3.49-3.44 6.33-6.93 6.93z" />
                        <path d="M13 7h-2v6h6v-2h-4z" />
                      </svg>
                    </div>
                    <h4>Subscription</h4>
                  </div>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="detail-label">Status</span>
                      <span className="detail-value">
                        {getSubscriptionBadge(selectedSchool)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Paid</span>
                      <span className="detail-value">
                        {selectedSchool.subscription_paid === false ? 'No' : 'Yes'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Start Date</span>
                      <span className="detail-value">{selectedSchool.subscription_start_date || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">End Date</span>
                      <span className="detail-value">{selectedSchool.subscription_end_date || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Warn Before</span>
                      <span className="detail-value">
                        {selectedSchool.subscription_warning_days !== undefined && selectedSchool.subscription_warning_days !== null
                          ? `${selectedSchool.subscription_warning_days} day(s)`
                          : '—'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="section-divider"></div>

                <div className="details-section">
                  <div className="section-header">
                    <div className="section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9S10.62 6.5 12 6.5S14.5 7.62 14.5 9S13.38 11.5 12 11.5Z" />
                      </svg>
                    </div>
                    <h4>Contact Information</h4>
                  </div>
                  <div className="details-grid">
                    <div className="detail-item full-width">
                      <span className="detail-label">Address</span>
                      <span className="detail-value">{selectedSchool.contact?.address || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">City</span>
                      <span className="detail-value">{selectedSchool.contact?.city || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">State</span>
                      <span className="detail-value">{selectedSchool.contact?.state || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">ZIP Code</span>
                      <span className="detail-value">{selectedSchool.contact?.zipCode || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Phone</span>
                      <span className="detail-value">{selectedSchool.contact?.phone || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email</span>
                      <span className="detail-value">{selectedSchool.contact?.email || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Website</span>
                      <span className="detail-value">{selectedSchool.contact?.website || '—'}</span>
                    </div>
                  </div>
                </div>

                <div className="section-divider"></div>

                <div className="details-section">
                  <div className="section-header">
                    <div className="section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
                      </svg>
                    </div>
                    <h4>Owner Information</h4>
                  </div>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="detail-label">Name</span>
                      <span className="detail-value">{selectedSchool.owner?.name || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email</span>
                      <span className="detail-value">{selectedSchool.owner?.email || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Phone</span>
                      <span className="detail-value">{selectedSchool.owner?.phone || '—'}</span>
                    </div>
                  </div>
                </div>

                <div className="section-divider"></div>

                <div className="details-section">
                  <div className="section-header">
                    <div className="section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H7V10H17V12ZM15 16H7V14H15V16ZM17 8H7V6H17V8Z" />
                      </svg>
                    </div>
                    <h4>Learning Providers</h4>
                  </div>
                  <div className="providers-details">
                    <div className="provider-count">
                      <span className="count-number">{(selectedSchool.accessible_providers || []).length}</span>
                      <span className="count-label">providers selected</span>
                    </div>
                    {selectedSchool.accessible_providers && selectedSchool.accessible_providers.length > 0 && (
                      <div className="provider-list-details">
                        {getProviderNames(selectedSchool.accessible_providers).map((providerName, index) => (
                          <span key={index} className="provider-badge">
                            {providerName}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* School-Specific Management Sections */}
                <div className="section-divider"></div>

                {/* Grades Management Section */}
                <div className="collapsible-section">
                  <div
                    className="collapsible-header"
                    onClick={() => setExpandedSections(prev => ({ ...prev, grades: !prev.grades }))}
                  >
                    <div className="section-header">
                      <div className="section-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" />
                        </svg>
                      </div>
                      <h4>Grades Management</h4>
                    </div>
                    <div className="collapsible-toggle">
                      <span className="section-count">{grades.length} grade{grades.length !== 1 ? 's' : ''} • {gradeSections.length} subsection{gradeSections.length !== 1 ? 's' : ''}</span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        style={{ transform: expandedSections.grades ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                      >
                        <path d="M7.41 8.58L12 13.17L16.59 8.58L18 10L12 16L6 10L7.41 8.58Z" />
                      </svg>
                    </div>
                  </div>
                  {expandedSections.grades && (
                    <div className="collapsible-content">
                      {loadingGrades ? (
                        <div className="loading-state">Loading grades...</div>
                      ) : (
                        <GradesManagementSection
                          grades={grades}
                          editingGrade={editingGrade}
                          setEditingGrade={setEditingGrade}
                          onCreate={createGrade}
                          onUpdate={updateGrade}
                          onDelete={deleteGrade}
                          gradeSections={gradeSections}
                          editingSection={editingSection}
                          setEditingSection={setEditingSection}
                          onCreateSection={createGradeSection}
                          onUpdateSection={updateGradeSection}
                          onDeleteSection={deleteGradeSection}
                        />
                      )}
                    </div>
                  )}
                </div>

                <div className="section-divider"></div>

                {/* Subjects Management Section */}
                <div className="collapsible-section">
                  <div
                    className="collapsible-header"
                    onClick={() => setExpandedSections(prev => ({ ...prev, subjects: !prev.subjects }))}
                  >
                    <div className="section-header">
                      <div className="section-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H7V10H17V12ZM15 16H7V14H15V16ZM17 8H7V6H17V8Z" />
                        </svg>
                      </div>
                      <h4>Subjects Management</h4>
                    </div>
                    <div className="collapsible-toggle">
                      <span className="section-count">{subjects.length} subject{subjects.length !== 1 ? 's' : ''}</span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        style={{ transform: expandedSections.subjects ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                      >
                        <path d="M7.41 8.58L12 13.17L16.59 8.58L18 10L12 16L6 10L7.41 8.58Z" />
                      </svg>
                    </div>
                  </div>
                  {expandedSections.subjects && (
                    <div className="collapsible-content">
                      {loadingSubjects ? (
                        <div className="loading-state">Loading subjects...</div>
                      ) : (
                        <SubjectsManagementSection
                          subjects={subjects}
                          editingSubject={editingSubject}
                          setEditingSubject={setEditingSubject}
                          onCreate={createSubject}
                          onUpdate={updateSubject}
                          onDelete={deleteSubject}
                        />
                      )}
                    </div>
                  )}
                </div>

                <div className="section-divider"></div>

                {/* Teachers Hierarchy Section */}
                <div className="collapsible-section">
                  <div
                    className="collapsible-header"
                    onClick={() => setExpandedSections(prev => ({ ...prev, teachers: !prev.teachers }))}
                  >
                    <div className="section-header">
                      <div className="section-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
                        </svg>
                      </div>
                      <h4>Teachers Hierarchy</h4>
                    </div>
                    <div className="collapsible-toggle">
                      <span className="section-count">{teachers.length} teacher{teachers.length !== 1 ? 's' : ''}</span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        style={{ transform: expandedSections.teachers ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                      >
                        <path d="M7.41 8.58L12 13.17L16.59 8.58L18 10L12 16L6 10L7.41 8.58Z" />
                      </svg>
                    </div>
                  </div>
                  {expandedSections.teachers && (
                    <div className="collapsible-content">
                      {loadingTeachers ? (
                        <div className="loading-state">Loading teachers...</div>
                      ) : (
                        <TeachersHierarchySection
                          teachers={teachers}
                          editingTeacher={editingTeacher}
                          setEditingTeacher={setEditingTeacher}
                          onUpdate={updateTeacherRole}
                        />
                      )}
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button type="button" className="school-cancel-btn" onClick={handleDeleteSchool}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                    </svg>
                    Delete School
                  </button>

                  <button type="button" className="school-submit-btn" onClick={startEditSelectedSchool}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.71 7.04C21.1 6.65 21.1 6 20.71 5.63L18.37 3.29C18 2.9 17.35 2.9 16.96 3.29L15.12 5.12L18.87 8.87M3 17.25V21H6.75L17.81 9.93L14.06 6.18L3 17.25Z" />
                    </svg>
                    Edit School
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleUpdateSchool} className="school-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">School Name *</label>
                    <input type="text" id="name" name="name" value={schoolFormData.name} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="school_id">School ID *</label>
                    <input
                      type="text"
                      id="school_id"
                      name="school_id"
                      value={schoolFormData.school_id}
                      readOnly
                      style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed', color: '#64748b' }}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="password">School Password *</label>
                    <input type="text" id="password" name="password" value={schoolFormData.password} onChange={handleInputChange} placeholder="Set school dashboard password" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="max_students">Max Students</label>
                    <input type="number" id="max_students" name="max_students" value={schoolFormData.max_students} onChange={handleInputChange} placeholder="e.g., 500" min="0" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="max_teachers">Max Teachers</label>
                    <input type="number" id="max_teachers" name="max_teachers" value={schoolFormData.max_teachers} onChange={handleInputChange} placeholder="e.g., 50" min="0" />
                  </div>
                </div>

                <div className="form-divider"></div>
                <div className="form-section-header"><h3>Subscription</h3></div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="subscription_start_date">Start Date</label>
                    <input
                      type="date"
                      id="subscription_start_date"
                      name="subscription_start_date"
                      value={schoolFormData.subscription_start_date || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subscription_end_date">End Date</label>
                    <input
                      type="date"
                      id="subscription_end_date"
                      name="subscription_end_date"
                      value={schoolFormData.subscription_end_date || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                  {/* Duration dropdown commented out - showing calculated duration below instead
                  <div className="form-group">
                    <label htmlFor="subscription_duration_months">Duration</label>
                    <select
                      id="subscription_duration_months"
                      name="subscription_duration_months"
                      value={schoolFormData.subscription_duration_months}
                      onChange={handleInputChange}
                    >
                      <option value="2">2 months</option>
                      <option value="6">6 months</option>
                      <option value="12">1 year</option>
                    </select>
                  </div>
                  */}
                </div>
                {schoolFormData.subscription_start_date && schoolFormData.subscription_end_date && (() => {
                  const start = new Date(schoolFormData.subscription_start_date);
                  const end = new Date(schoolFormData.subscription_end_date);
                  const diffMs = end - start;
                  if (diffMs <= 0) return null;
                  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                  let durationText = '';
                  if (diffDays >= 365) {
                    const years = Math.floor(diffDays / 365);
                    const remainingDays = diffDays % 365;
                    const months = Math.floor(remainingDays / 30);
                    durationText = years === 1 ? '1 year' : `${years} years`;
                    if (months > 0) durationText += ` ${months} month${months > 1 ? 's' : ''}`;
                  } else if (diffDays >= 30) {
                    const months = Math.floor(diffDays / 30);
                    const remainingDays = diffDays % 30;
                    durationText = months === 1 ? '1 month' : `${months} months`;
                    if (remainingDays >= 7) {
                      const weeks = Math.floor(remainingDays / 7);
                      durationText += ` ${weeks} week${weeks > 1 ? 's' : ''}`;
                    }
                  } else if (diffDays >= 7) {
                    const weeks = Math.floor(diffDays / 7);
                    const remainingDays = diffDays % 7;
                    durationText = weeks === 1 ? '1 week' : `${weeks} weeks`;
                    if (remainingDays > 0) durationText += ` ${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
                  } else {
                    durationText = diffDays === 1 ? '1 day' : `${diffDays} days`;
                  }
                  return <div className="field-hint" style={{ marginTop: '-12px', marginBottom: '24px', color: '#667085', fontWeight: '500' }}>Duration: {durationText}</div>;
                })()}
                {/* Warn Before field commented out for better UI
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="subscription_warning_days">Warn Before (days)</label>
                    <div className="input-suffix">
                      <input
                        type="number"
                        id="subscription_warning_days"
                        name="subscription_warning_days"
                        value={schoolFormData.subscription_warning_days}
                        onChange={handleInputChange}
                        min="0"
                      />
                      <span className="suffix">days</span>
                    </div>
                    <div className="field-hint">Shows a warning in the school dashboard when nearing the end date.</div>
                  </div>
                </div>
                */}
                <div className="form-row">
                  <div className="form-group full-width">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="subscription_paid"
                        checked={Boolean(schoolFormData.subscription_paid)}
                        onChange={handleInputChange}
                      />
                      Paid (users can access)
                    </label>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <label style={{ margin: 0 }}>Select Providers *</label>
                      <button
                        type="button"
                        onClick={handleSelectAllProviders}
                        style={{
                          padding: '6px 12px',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: '#3b82f6',
                          background: 'transparent',
                          border: '1px solid #3b82f6',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#eff6ff';
                          e.target.style.borderColor = '#2563eb';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                          e.target.style.borderColor = '#3b82f6';
                        }}
                      >
                        {providers.filter(p => p.is_active !== false).length > 0 && 
                         providers.filter(p => p.is_active !== false).every(p => schoolFormData.selectedProviders.includes(p.id))
                          ? 'Deselect All' 
                          : 'Select All'}
                      </button>
                    </div>
                    <div className="providers-checkbox-grid">
                      {providers
                        .filter(provider => provider.is_active !== false)
                        .map((provider) => (
                          <div key={provider.id} className="provider-checkbox-item">
                            <input type="checkbox" id={`edit-provider-${provider.id}`} checked={schoolFormData.selectedProviders.includes(provider.id)} onChange={() => handleProviderToggle(provider.id)} />
                            <label htmlFor={`edit-provider-${provider.id}`}>
                              <span className="provider-name">{provider.name || 'Unnamed Provider'}</span>
                              {/* <span className="provider-key">{provider.key || 'No key'}</span> */}
                            </label>
                          </div>
                        ))}
                    </div>
                    {providers.filter(provider => provider.is_active !== false).length > 0 && schoolFormData.selectedProviders.length === 0 && (
                      <p className="provider-validation-text">⚠️ Please select at least one provider</p>
                    )}
                    {providers.filter(provider => provider.is_active !== false).length === 0 && (
                      <p className="no-providers-text">No active providers available. Add providers first.</p>
                    )}
                  </div>
                </div>

                <div className="form-divider"></div>
                <div className="form-section-header"><h3>Contact Information</h3></div>
                <div className="form-row">
                  <div className="form-group full-width">
                    <label htmlFor="address">School Address *</label>
                    <textarea id="address" name="address" value={schoolFormData.address} onChange={handleInputChange} rows="3" required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City</label>
                    <input type="text" id="city" name="city" value={schoolFormData.city} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="state">State</label>
                    <input type="text" id="state" name="state" value={schoolFormData.state} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="zipCode">ZIP Code</label>
                    <input type="text" id="zipCode" name="zipCode" value={schoolFormData.zipCode} onChange={handleInputChange} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input type="tel" id="phone" name="phone" value={schoolFormData.phone} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input type="email" id="email" name="email" value={schoolFormData.email} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="website">Website</label>
                    <input type="url" id="website" name="website" value={schoolFormData.website} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="form-divider"></div>
                <div className="form-section-header"><h3>Owner Information</h3></div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="ownerName">Owner Name *</label>
                    <input type="text" id="ownerName" name="ownerName" value={schoolFormData.ownerName} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ownerEmail">Owner Email</label>
                    <input type="email" id="ownerEmail" name="ownerEmail" value={schoolFormData.ownerEmail} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ownerPhone">Owner Phone *</label>
                    <input type="tel" id="ownerPhone" name="ownerPhone" value={schoolFormData.ownerPhone} onChange={handleInputChange} required />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="school-cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
                  <button type="submit" className="school-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Changes'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Discard Changes Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="unsaved-changes-overlay" onClick={cancelDiscard}>
          <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-changes-header">
              <div className="unsaved-changes-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Unsaved Changes</h2>
            </div>
            <div className="unsaved-changes-body">
              <p className="unsaved-changes-message">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>
            <div className="unsaved-changes-actions">
              <button className="unsaved-changes-cancel-btn" onClick={cancelDiscard}>
                Cancel
              </button>
              <button className="unsaved-changes-discard-btn" onClick={confirmDiscard}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddSchoolModal && (
        <div className="modal-overlay" onClick={() => handleOverlayClick(closeModal)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New School - School Details</h2>
              <button className="modal-close" onClick={() => handleOverlayClick(closeModal)}>×</button>
            </div>

            <form onSubmit={handleSubmitSchool} className="school-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">School Name *</label>
                  <input type="text" id="name" name="name" value={schoolFormData.name} onChange={handleInputChange} placeholder="Enter school name" required />
                </div>
                <div className="form-group">
                  <label htmlFor="school_id">School ID *</label>
                  <input
                    type="text"
                    id="school_id"
                    name="school_id"
                    value={schoolFormData.school_id}
                    onChange={handleInputChange}
                    placeholder="Enter unique school ID"
                    required
                    className={schoolIdError ? 'error-input' : ''}
                  />
                  <div className="school-id-validation-container">
                    {isCheckingId && <div className="checking-id">Checking availability...</div>}
                    {schoolIdError && <div className="id-error error-message">{schoolIdError}</div>}
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">School Password *</label>
                  <input type="text" id="password" name="password" value={schoolFormData.password} onChange={handleInputChange} placeholder="Set school dashboard password" required />
                </div>
                <div className="form-group">
                  <label htmlFor="max_students">Max Students</label>
                  <input type="number" id="max_students" name="max_students" value={schoolFormData.max_students} onChange={handleInputChange} placeholder="e.g., 500" min="0" />
                </div>
                <div className="form-group">
                  <label htmlFor="max_teachers">Max Teachers</label>
                  <input type="number" id="max_teachers" name="max_teachers" value={schoolFormData.max_teachers} onChange={handleInputChange} placeholder="e.g., 50" min="0" />
                </div>
              </div>

              <div className="form-divider"></div>
              <div className="form-section-header"><h3>Subscription</h3></div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="subscription_start_date">Start Date</label>
                  <input
                    type="date"
                    id="subscription_start_date"
                    name="subscription_start_date"
                    value={schoolFormData.subscription_start_date || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subscription_end_date">End Date</label>
                  <input
                    type="date"
                    id="subscription_end_date"
                    name="subscription_end_date"
                    value={schoolFormData.subscription_end_date || ''}
                    onChange={handleInputChange}
                  />
                </div>
                {/* Duration dropdown commented out - showing calculated duration below instead
                <div className="form-group">
                  <label htmlFor="subscription_duration_months">Duration</label>
                  <select
                    id="subscription_duration_months"
                    name="subscription_duration_months"
                    value={schoolFormData.subscription_duration_months}
                    onChange={handleInputChange}
                  >
                    <option value="2">2 months</option>
                    <option value="6">6 months</option>
                    <option value="12">1 year</option>
                  </select>
                </div>
                */}
              </div>
              {schoolFormData.subscription_start_date && schoolFormData.subscription_end_date && (() => {
                const start = new Date(schoolFormData.subscription_start_date);
                const end = new Date(schoolFormData.subscription_end_date);
                const diffMs = end - start;
                if (diffMs <= 0) return null;
                const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                let durationText = '';
                if (diffDays >= 365) {
                  const years = Math.floor(diffDays / 365);
                  const remainingDays = diffDays % 365;
                  const months = Math.floor(remainingDays / 30);
                  durationText = years === 1 ? '1 year' : `${years} years`;
                  if (months > 0) durationText += ` ${months} month${months > 1 ? 's' : ''}`;
                } else if (diffDays >= 30) {
                  const months = Math.floor(diffDays / 30);
                  const remainingDays = diffDays % 30;
                  durationText = months === 1 ? '1 month' : `${months} months`;
                  if (remainingDays >= 7) {
                    const weeks = Math.floor(remainingDays / 7);
                    durationText += ` ${weeks} week${weeks > 1 ? 's' : ''}`;
                  }
                } else if (diffDays >= 7) {
                  const weeks = Math.floor(diffDays / 7);
                  const remainingDays = diffDays % 7;
                  durationText = weeks === 1 ? '1 week' : `${weeks} weeks`;
                  if (remainingDays > 0) durationText += ` ${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
                } else {
                  durationText = diffDays === 1 ? '1 day' : `${diffDays} days`;
                }
                return <div className="field-hint" style={{ marginTop: '-12px', marginBottom: '24px', color: '#667085', fontWeight: '500' }}>Duration: {durationText}</div>;
              })()}
              {/* Warn Before field commented out for better UI
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="subscription_warning_days">Warn Before (days)</label>
                  <div className="input-suffix">
                    <input
                      type="number"
                      id="subscription_warning_days"
                      name="subscription_warning_days"
                      value={schoolFormData.subscription_warning_days}
                      onChange={handleInputChange}
                      min="0"
                    />
                    <span className="suffix">days</span>
                  </div>
                  <div className="field-hint">Shows a warning in the school dashboard when nearing the end date.</div>
                </div>
              </div>
              */}
              <div className="form-row">
                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="subscription_paid"
                      checked={Boolean(schoolFormData.subscription_paid)}
                      onChange={handleInputChange}
                    />
                    Paid (users can access)
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <label style={{ margin: 0 }}>Select Providers *</label>
                    <button
                      type="button"
                      onClick={handleSelectAllProviders}
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#3b82f6',
                        background: 'transparent',
                        border: '1px solid #3b82f6',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#eff6ff';
                        e.target.style.borderColor = '#2563eb';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.borderColor = '#3b82f6';
                      }}
                    >
                      {providers.filter(p => p.is_active !== false).length > 0 && 
                       providers.filter(p => p.is_active !== false).every(p => schoolFormData.selectedProviders.includes(p.id))
                        ? 'Deselect All' 
                        : 'Select All'}
                    </button>
                  </div>
                  <div className="providers-checkbox-grid">
                    {providers
                      .filter(provider => provider.is_active !== false)
                      .map((provider) => (
                        <div key={provider.id} className="provider-checkbox-item">
                          <input type="checkbox" id={`provider-${provider.id}`} checked={schoolFormData.selectedProviders.includes(provider.id)} onChange={() => handleProviderToggle(provider.id)} />
                          <label htmlFor={`provider-${provider.id}`}>
                            <span className="provider-name">{provider.name || 'Unnamed Provider'}</span>
                            {/* <span className="provider-key">{provider.key || 'No key'}</span> */}
                          </label>
                        </div>
                      ))}
                  </div>
                  {providers.filter(provider => provider.is_active !== false).length === 0 && (
                    <p className="no-providers-text">No active providers available. Add providers first.</p>
                  )}
                  {providers.filter(provider => provider.is_active !== false).length > 0 && schoolFormData.selectedProviders.length === 0 && (
                    <p className="provider-validation-text">⚠️ Please select at least one provider</p>
                  )}
                </div>
              </div>

              <div className="form-divider"></div>
              <div className="form-section-header"><h3>Contact Information</h3></div>
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="address">School Address *</label>
                  <textarea id="address" name="address" value={schoolFormData.address} onChange={handleInputChange} placeholder="Enter school address" rows="3" required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input type="text" id="city" name="city" value={schoolFormData.city} onChange={handleInputChange} placeholder="Enter city" />
                </div>
                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <input type="text" id="state" name="state" value={schoolFormData.state} onChange={handleInputChange} placeholder="Enter state" />
                </div>
                <div className="form-group">
                  <label htmlFor="zipCode">ZIP Code</label>
                  <input type="text" id="zipCode" name="zipCode" value={schoolFormData.zipCode} onChange={handleInputChange} placeholder="Enter ZIP code" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input type="tel" id="phone" name="phone" value={schoolFormData.phone} onChange={handleInputChange} placeholder="Enter phone number" required />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input type="email" id="email" name="email" value={schoolFormData.email} onChange={handleInputChange} placeholder="Enter email address" required />
                </div>
                <div className="form-group">
                  <label htmlFor="website">Website</label>
                  <input type="url" id="website" name="website" value={schoolFormData.website} onChange={handleInputChange} placeholder="Enter website URL" />
                </div>
              </div>

              <div className="form-divider"></div>
              <div className="form-section-header"><h3>Owner Information</h3></div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ownerName">Owner Name *</label>
                  <input type="text" id="ownerName" name="ownerName" value={schoolFormData.ownerName} onChange={handleInputChange} placeholder="Enter owner name" required />
                </div>
                <div className="form-group">
                  <label htmlFor="ownerEmail">Owner Email</label>
                  <input type="email" id="ownerEmail" name="ownerEmail" value={schoolFormData.ownerEmail} onChange={handleInputChange} placeholder="Enter owner email" />
                </div>
                <div className="form-group">
                  <label htmlFor="ownerPhone">Owner Phone *</label>
                  <input type="tel" id="ownerPhone" name="ownerPhone" value={schoolFormData.ownerPhone} onChange={handleInputChange} placeholder="Enter owner phone" required />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="school-cancel-btn" onClick={() => handleOverlayClick(closeModal)}>Cancel</button>
                <button type="submit" className="school-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Adding School...' : 'Add School'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="modal-content filter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Filter Schools</h2>
              <button className="modal-close" onClick={() => setShowFilterModal(false)}>×</button>
            </div>

            <div className="filter-content">
              <div className="filter-section">
                <h4>Filter By</h4>
                <div className="filter-options">
                  <label className="filter-option">
                    <input type="radio" name="filterType" value="alphabet" checked={filterType === 'alphabet'} onChange={(e) => setFilterType(e.target.value)} />
                    Alphabetical Order
                  </label>
                  <label className="filter-option">
                    <input type="radio" name="filterType" value="provider" checked={filterType === 'provider'} onChange={(e) => setFilterType(e.target.value)} />
                    Provider
                  </label>
                </div>
              </div>

              {filterType === 'provider' && (
                <div className="filter-section">
                  <h4>Provider Name</h4>
                  <select value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
                    <option value="">All Providers</option>
                    {providers
                      .filter(provider => provider.is_active !== false)
                      .map(provider => (
                        <option key={provider.id} value={provider.name}>{provider.name}</option>
                      ))}
                  </select>
                </div>
              )}

              <div className="filter-actions">
                <button className="school-cancel-btn" onClick={() => {
                  setFilterType('alphabet');
                  setFilterValue('');
                  setShowFilterModal(false);
                }}>Clear Filters</button>
                <button className="school-submit-btn" onClick={() => setShowFilterModal(false)}>Apply Filters</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && itemToDelete && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className=" confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-header">
              <div className="confirmation-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
              </div>
              <h2>Delete {itemToDelete.type === 'school' ? 'School' : 'User'}</h2>
            </div>

            <div className="confirmation-content">
              <p className="confirmation-message">
                Are you sure you want to delete <strong>"{itemToDelete.data.name || itemToDelete.data.first_name + ' ' + itemToDelete.data.last_name}"</strong>?
              </p>
              <p className="confirmation-warning">
                This action cannot be undone and will permanently remove all associated data.
              </p>
            </div>

            <div className="confirmation-actions">
              <button className="cancel-delete-btn" onClick={cancelDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </svg>
                Cancel
              </button>
              <button className="confirm-delete-btn" onClick={confirmDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
                Delete {itemToDelete.type === 'school' ? 'School' : 'User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && selectedSchoolForUser && (
        <div className="modal-overlay" onClick={() => handleUserOverlayClick(closeAddUserModal)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add User to {selectedSchoolForUser.name}</h2>
              <button className="modal-close" onClick={() => handleUserOverlayClick(closeAddUserModal)}>×</button>
            </div>

            <div className="add-user-options">
              <div className="option-buttons">
                <button
                  className={`option-btn ${addUserMode === 'single' ? 'active' : ''}`}
                  onClick={() => setAddUserMode('single')}
                >
                  <UserIcon />
                  Add Single User
                </button>
                <button
                  className={`option-btn ${addUserMode === 'excel' ? 'active' : ''}`}
                  onClick={() => setAddUserMode('excel')}
                >
                  <ExcelIcon />
                  Upload Excel File
                </button>
              </div>

              {addUserMode === 'single' ? (
                <form onSubmit={handleSingleUserSubmit} className="school-user-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="add_first_name">First Name *</label>
                      <input type="text" id="add_first_name" name="first_name" value={userFormData.first_name} onChange={handleUserInputChange} required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="add_last_name">Last Name *</label>
                      <input type="text" id="add_last_name" name="last_name" value={userFormData.last_name} onChange={handleUserInputChange} required />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="add_school_id">School Name *</label>
                      <select id="add_school_id" name="school_id" value={userFormData.school_id} onChange={handleUserInputChange} required>
                        <option value="">Select school</option>
                        {schools.map(school => (
                          <option key={school.id} value={school.id}>{school.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="add_email">Email Address *</label>
                      <input type="email" id="add_email" name="email" value={userFormData.email} onChange={handleUserInputChange} required />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="add_phone">Phone Number</label>
                      <input type="tel" id="add_phone" name="phone" value={userFormData.phone} onChange={handleUserInputChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="add_role">Role *</label>
                      <select id="add_role" name="role" value={userFormData.role} onChange={handleUserInputChange} required>
                        <option value="">Select role</option>
                        <option value="admin">Admin</option>
                        <option value="teacher">Teacher</option>
                        <option value="student">Student</option>
                        {/* <option value="parent">Parent</option> */}
                        {/* <option value="staff">Staff</option> */}
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Grade</label>
                      <div className="grade-selection">
                        <div className="grade-type-selection">
                          <label className="grade-type-option">
                            <input
                              type="radio"
                              name="addGradeType"
                              value="KG"
                              checked={gradeType === 'KG'}
                              onChange={(e) => handleGradeTypeChange(e.target.value)}
                            />
                            <span>KG</span>
                          </label>
                          <label className="grade-type-option">
                            <input
                              type="radio"
                              name="addGradeType"
                              value="Grade"
                              checked={gradeType === 'Grade'}
                              onChange={(e) => handleGradeTypeChange(e.target.value)}
                            />
                            <span>Grade</span>
                          </label>
                        </div>

                        {gradeType === 'KG' && (
                          <div className="grade-level-selection">
                            <label className="grade-level-option">
                              <input
                                type="radio"
                                name="addKgLevel"
                                value="1"
                                checked={gradeLevel === '1'}
                                onChange={(e) => handleGradeLevelChange(e.target.value)}
                              />
                              <span>KG1</span>
                            </label>
                            <label className="grade-level-option">
                              <input
                                type="radio"
                                name="addKgLevel"
                                value="2"
                                checked={gradeLevel === '2'}
                                onChange={(e) => handleGradeLevelChange(e.target.value)}
                              />
                              <span>KG2</span>
                            </label>
                            <label className="grade-level-option">
                              <input
                                type="radio"
                                name="addKgLevel"
                                value="3"
                                checked={gradeLevel === '3'}
                                onChange={(e) => handleGradeLevelChange(e.target.value)}
                              />
                              <span>KG3</span>
                            </label>
                          </div>
                        )}

                        {gradeType === 'Grade' && (
                          <div className="grade-level-selection">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
                              <label key={num} className="grade-level-option">
                                <input
                                  type="radio"
                                  name="addGradeLevel"
                                  value={num.toString()}
                                  checked={gradeLevel === num.toString()}
                                  onChange={(e) => handleGradeLevelChange(e.target.value)}
                                />
                                <span>Grade {num}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* <div className="form-group">
                      <label htmlFor="add_language">Language</label>
                      <select id="add_language" name="language" value={userFormData.language} onChange={handleUserInputChange}>
                        <option value="">Select language</option>
                        <option value="English">English</option>
                        <option value="French">French</option>
                        <option value="Arabic">Arabic</option>
                        <option value="Other">Other</option>
                      </select>
                    </div> */}
                  </div>

                  <div className="form-divider"></div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={userFormData.is_active === true}
                          onChange={(e) => setUserFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                        />
                        <span>Active</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="school-cancel-btn" onClick={() => handleUserOverlayClick(closeAddUserModal)}>Cancel</button>
                    <button type="submit" className="school-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Adding User...' : 'Add User'}</button>
                  </div>
                </form>
              ) : (
                <div className="excel-upload-section">
                  <div className="upload-area">
                    <input
                      type="file"
                      id="excelFile"
                      accept=".xlsx,.xls"
                      onChange={handleExcelFileChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="excelFile" className="upload-label">
                      <ExcelIcon />
                      <span>Choose Excel File</span>
                      <small>Supported formats: .xlsx, .xls</small>
                    </label>
                    {excelFile && (
                      <div className="file-info">
                        <span>Selected: {excelFile.name}</span>
                      </div>
                    )}
                  </div>

                  <div className="excel-template-info">
                    <h4>Excel Template Format:</h4>
                    <p>Your Excel file should have the following columns:</p>
                    <ul>
                      <li>UserId (optional)</li>
                      <li>Role (student, teacher, admin)</li>
                      <li>First Name / Last Name (or FullName)</li>
                      <li>Email</li>
                      <li>Password (optional)</li>
                      <li>Phone Number (optional — typically teachers only)</li>
                      {/* <li>Language (optional)</li> */}
                      <li>Grade (optional — teachers can list multiple like "Grade 5, Grade 8")</li>
                      <li>School (recommended — must match selected school)</li>
                      <li>Status (Active/Inactive)</li>
                      <li>Subject (required for teachers; leave empty for students)</li>
                    </ul>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="school-cancel-btn" onClick={() => handleUserOverlayClick(closeAddUserModal)}>Cancel</button>
                    <button
                      type="button"
                      className="school-submit-btn"
                      onClick={handleExcelUpload}
                      disabled={!excelFile || isExcelUploading}
                    >
                      {isExcelUploading ? 'Importing...' : 'Upload & Add Users'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        autoClose={true}
        autoCloseDelay={4000}
      />
    </div>
  );
};

export default DashboardSchool;


